/**
 * Tools Registry
 * Central registry of all tools available on InventiveHQ.com
 */

/**
 * HowTo step for Schema.org structured data
 * Used to generate HowTo rich snippets in search results
 */
export interface HowToStep {
  name: string;        // Step title (e.g., "Enter your input")
  text: string;        // Step description
  image?: string;      // Optional image URL for the step
}

export interface Tool {
  name: string;
  description: string;
  category: string;
  secondary_categories?: string[]; // Tools can appear in multiple category listings
  link: string;
  icon: string;
  /**
   * How this tool renders when embedded in an article via `<div data-tool="…">`.
   * 'card' (for tall/stateful tools — wizards, planners, assessors, simulators,
   * multi-step builders) renders a compact <ToolPreviewCard> linking to the
   * canonical tool page, because a full inline render is too tall and swamps the
   * surrounding content. 'inline' (or omitted) renders the live tool inline.
   * 'hero' renders the tool full-width ABOVE the article/TOC grid when its embed
   * leads the post content (blog/[slug]/page.tsx extracts it); embedded
   * mid-article it falls back to the compact <ToolPreviewCard>.
   */
  embed?: 'inline' | 'card' | 'hero';
  comingSoon?: boolean;
  enabled?: boolean; // Set to false to hide from listings (default: true)
  howToSteps?: HowToStep[]; // Optional custom steps for HowTo schema (generic steps generated if not provided)
}

export interface ToolCategory {
  name: string;        // Display name: "Security"
  slug: string;        // URL slug: "security"
  description: string;
  icon: string;        // Lucide icon name
  longDescription?: string;
  useCases?: string[];
  faqs?: { question: string; answer: string }[];
}

export const toolCategories: ToolCategory[] = [
  {
    name: 'Random',
    slug: 'random',
    description: 'Random pickers, dice, spinners, and probability simulators that run entirely in your browser',
    icon: 'Dices',
    longDescription: 'Fair random generators and probability demonstrations — name wheels and team pickers for classrooms and stand-ups, dice for tabletop games, coin flips, and simulators that show how probability actually behaves over thousands of trials.\n\nEverything runs client-side using the browser\'s crypto-backed random source where randomness matters. Nothing you enter is uploaded, so a wheel full of real names or a team roster stays on your device.',
    useCases: [
      'Teachers and facilitators drawing names, picking teams, or choosing an order at random',
      'Tabletop and D&D players needing dice when the physical set is not to hand',
      'Students seeing the law of large numbers and the central limit theorem emerge from real trials',
      'Anyone settling a decision with a coin flip, spinner, or yes/no wheel',
    ],
    faqs: [
      { question: 'Are these generators actually random?', answer: 'The pickers and dice draw from the browser\'s cryptographically secure random source rather than a simple pseudo-random sequence, so results are not predictable from previous draws. The teaching simulators use ordinary pseudo-random numbers, which is appropriate for demonstrating distributions.' },
      { question: 'Do the names or lists I enter get uploaded?', answer: 'No. Every tool in this category runs entirely in your browser. Names, team rosters, and custom wheel entries never leave your device and are not stored on any server.' },
      { question: 'Why do the probability simulators need thousands of trials?', answer: 'Because that is the point. Small samples look lumpy and unfair; the convergence toward the expected value only becomes visible over many trials, which is exactly what the law of large numbers and central limit theorem describe.' },
    ],
  },
  {
    name: 'Planning',
    slug: 'planning',
    description: 'Strategic planning and budgeting tools for IT and cybersecurity initiatives',
    icon: 'Wrench',
    longDescription: 'Our planning tools help you calculate budgets, estimate breach costs, assess vendor risks, and justify security investments with ROI analysis. Whether you\'re building a business case for new security controls or evaluating cloud migration costs, these calculators use industry benchmarks from IBM, Gartner, and NIST frameworks to provide data-driven estimates.\n\nFrom cybersecurity budget allocation to disaster recovery planning, these tools help IT leaders make informed decisions. Each calculator includes methodology explanations so you understand how the numbers are derived—not just what they are.',
    useCases: [
      'CISOs building annual cybersecurity budget proposals for executive approval',
      'IT managers calculating total cost of ownership for cloud migrations',
      'Risk managers quantifying potential breach impact in financial terms',
      'MSPs scoping client engagements and creating accurate project estimates',
    ],
    faqs: [
      { question: 'How accurate are the budget calculators?', answer: 'Our calculators use industry benchmarks from sources like IBM\'s Cost of a Data Breach Report, Gartner research, and NIST guidelines. Results should be used as starting points for planning—actual costs vary based on your specific environment, vendor relationships, and implementation approach.' },
      { question: 'Can I export or share the results?', answer: 'Most planning tools include PDF export functionality. You can generate professional reports suitable for budget proposals, board presentations, or client deliverables. Results include methodology notes explaining how figures were calculated.' },
      { question: 'Which compliance frameworks do these tools support?', answer: 'Our planning tools reference requirements from HIPAA, PCI-DSS, SOC 2, GDPR, and NIST CSF. The cybersecurity budget calculator and maturity assessment both factor in compliance requirements when generating recommendations.' },
      { question: 'How can I use these results in compliance documentation?', answer: 'Calculator outputs can support risk assessments, budget justifications, and business impact analyses required by frameworks like NIST and ISO 27001. The results document your methodology and assumptions, which auditors appreciate.' },
    ],
  },
  {
    name: 'Security',
    slug: 'security',
    description: 'Security analysis, password generation, encryption, and threat intelligence tools',
    icon: 'Shield',
    longDescription: 'Our browser-based security tools handle password testing, CVE lookups, JWT decoding, encryption, and threat intelligence—all without sending your data to external servers. Every tool processes data locally in your browser, ensuring sensitive information never leaves your device.\n\nFrom generating secure passwords to analyzing email headers for phishing indicators, these tools support the daily work of security professionals. Whether you\'re investigating a suspicious domain, decoding a malicious payload, or generating cryptographic keys, you\'ll find utilities that work offline and respect your privacy.',
    useCases: [
      'Security analysts investigating phishing campaigns and suspicious domains',
      'Developers implementing authentication, encryption, and secure configurations',
      'IT administrators auditing security headers and email authentication',
      'Penetration testers performing reconnaissance and payload analysis',
    ],
    faqs: [
      { question: 'Is my data safe when using these tools?', answer: 'Yes. All security tools process data locally in your browser using JavaScript. Passwords, keys, and sensitive data never leave your device. You can verify this by using browser developer tools to monitor network requests—you\'ll see no data is transmitted.' },
      { question: 'Do I need to create an account?', answer: 'No. All tools are free to use without registration. We don\'t track individual usage or require sign-ups. Your data stays on your device, and we have no way to access what you\'re analyzing.' },
      { question: 'Can I use these tools for professional security work?', answer: 'Absolutely. Many security professionals use our tools for threat intelligence, incident response, and security assessments. The CVE lookup, MITRE ATT&CK browser, and IOC extractor are particularly popular for SOC work.' },
      { question: 'How current are the vulnerability databases?', answer: 'CVE and CWE lookups query live data from official sources (NIST NVD, MITRE). Results reflect the latest published information. For time-sensitive vulnerability research, always verify against primary sources.' },
      { question: 'What\'s the advantage of browser-based tools vs. installed software?', answer: 'Browser tools require no installation, work across operating systems, and can\'t be flagged by endpoint security as potentially unwanted programs. They\'re ideal for quick analysis without modifying your system or requiring admin privileges.' },
    ],
  },
  {
    name: 'Network',
    slug: 'network',
    description: 'Network diagnostics, DNS, IP, and connectivity analysis tools',
    icon: 'Network',
    longDescription: 'Essential networking utilities for DNS lookups, subnet calculations, WHOIS queries, IP geolocation, and BGP AS lookups. These tools help you troubleshoot connectivity issues, investigate suspicious IP addresses, and plan network architectures.\n\nWhether you\'re debugging why a domain isn\'t resolving, calculating subnet ranges for a new office, or investigating where traffic is coming from, these tools provide the quick answers network professionals need. Results include explanations to help you understand what you\'re seeing.',
    useCases: [
      'Network administrators troubleshooting DNS resolution and routing issues',
      'Security analysts investigating suspicious IP addresses and domains',
      'DevOps engineers planning subnet allocations and CIDR ranges',
      'Developers debugging redirect chains and URL routing problems',
    ],
    faqs: [
      { question: 'Why might DNS lookup results differ from what I see locally?', answer: 'DNS results vary based on geographic location, DNS provider, and cache timing. Our lookups query authoritative nameservers directly. If you see different results locally, your ISP or local resolver may have cached older records. TTL values in results indicate how long records are cached.' },
      { question: 'How accurate is WHOIS information?', answer: 'WHOIS data comes from domain registrars and varies in completeness. Privacy protection services may obscure registrant details. For security investigations, WHOIS provides useful metadata like registration dates and nameservers even when contact info is redacted.' },
      { question: 'Can I use the subnet calculator for IPv6?', answer: 'Yes, the subnet calculator supports both IPv4 and IPv6 addressing. IPv6 calculations include prefix lengths, address ranges, and network/host portions. We also offer a dedicated IPv4/IPv6 converter for format translation.' },
      { question: 'How do I check if an IP address is malicious?', answer: 'Start with the IP Geolocation Lookup to identify the network owner and location. For reputation data, use our IP Risk Checker which queries threat intelligence feeds. Combine this with BGP AS Lookup to understand the hosting infrastructure.' },
    ],
  },
  {
    name: 'Developer',
    slug: 'developer',
    description: 'Development utilities, encoders, decoders, and code generators',
    icon: 'Code',
    longDescription: 'Productivity tools for developers: encode/decode data, format JSON, convert between formats, generate UUIDs, and build commands visually. Designed for speed with keyboard shortcuts, copy-to-clipboard functionality, and offline support.\n\nThese tools eliminate context-switching. Instead of searching Stack Overflow for cron syntax or JWT structure, get instant answers in your browser. Whether you\'re debugging API responses, generating test data, or building Docker commands, you\'ll find tools that integrate into your workflow.',
    useCases: [
      'Backend developers debugging API requests, webhooks, and data encoding',
      'Frontend engineers converting between JSON, YAML, and other data formats',
      'DevOps teams building Docker, AWS, and cron commands visually',
      'QA engineers generating mock data and testing regex patterns',
    ],
    faqs: [
      { question: 'Do these tools work offline?', answer: 'Most developer tools work entirely offline after initial page load. JSON formatting, encoding/decoding, and regex testing all run locally in your browser. Bookmark frequently-used tools for instant access even without internet.' },
      { question: 'Can I process large files?', answer: 'Browser-based tools can handle files up to several megabytes depending on your device\'s memory. For very large files (100MB+), consider using command-line alternatives. Most tools show progress indicators for larger operations.' },
      { question: 'Are the generators production-ready?', answer: 'UUID generators produce valid v4 UUIDs suitable for production use. Mock data generators create realistic test data but should not be used as real personal information. Always review generated code/commands before running in production environments.' },
      { question: 'What format conversions are supported?', answer: 'Our Data Format Converter handles JSON, YAML, XML, TOML, and CSV bidirectionally. For specific conversions, we also offer dedicated tools like CSV to JSON, YAML to JSON, and Excel to JSON converters.' },
    ],
  },
  {
    name: 'Media',
    slug: 'media',
    description: 'Image converters, QR codes, and visual content generation tools',
    icon: 'Image',
    longDescription: 'Convert images between formats (WebP, AVIF, HEIC, JPG, PNG), generate QR codes, and create word clouds—all processed locally in your browser. Your images never leave your device, making these tools safe for sensitive or proprietary content.\n\nThese tools solve common format compatibility issues. iPhone HEIC photos that won\'t open on Windows? WebP images you need as GIFs? Modern formats like AVIF that your software doesn\'t support? Upload, convert, and download—no cloud processing, no accounts, no compression quality loss from re-encoding.',
    useCases: [
      'Content creators converting iPhone HEIC photos for cross-platform sharing',
      'Web developers optimizing images to WebP and AVIF for faster load times',
      'Marketing teams generating QR codes for campaigns and print materials',
      'Designers creating word clouds for presentations and infographics',
    ],
    faqs: [
      { question: 'Do my images get uploaded to a server?', answer: 'No. All image processing happens locally in your browser using JavaScript and WebAssembly. Your images never leave your device. This makes our tools safe for confidential photos, proprietary designs, and sensitive documents.' },
      { question: 'Why should I convert images to WebP?', answer: 'WebP typically reduces file size by 25-35% compared to JPEG at equivalent quality, and supports transparency unlike JPEG. This means faster website load times and lower bandwidth costs. Most modern browsers support WebP.' },
      { question: 'Can I convert animated GIFs?', answer: 'Yes, our WebP to GIF converter preserves animation frames. The converter handles both static and animated WebP files, maintaining timing and loop settings in the output GIF.' },
      { question: 'What QR code error correction levels are available?', answer: 'QR codes support four error correction levels: L (7%), M (15%), Q (25%), and H (30%). Higher levels make codes more readable when damaged but increase density. For print materials, we recommend Level M or higher.' },
    ],
  },
  {
    name: 'Compliance',
    slug: 'compliance',
    description: 'Compliance checking, audit preparation, and regulatory alignment tools',
    icon: 'FileCheck',
    longDescription: 'Self-assess your organization against HIPAA, GDPR, and other regulatory frameworks. Generate privacy policies. Identify compliance gaps before formal audits discover them. These tools guide you through requirements with plain-language explanations.\n\nCompliance tools are educational aids, not substitutes for legal counsel. Use them to understand requirements, identify areas needing attention, and prepare documentation—then work with qualified professionals for official certifications and legal review.',
    useCases: [
      'Healthcare organizations conducting HIPAA self-assessments before audits',
      'Startups generating initial privacy policies for apps and websites',
      'Compliance officers identifying gaps and prioritizing remediation efforts',
      'Legal teams evaluating GDPR obligations for European user data',
    ],
    faqs: [
      { question: 'Can these tools certify my organization as compliant?', answer: 'No. These are self-assessment and educational tools. Official compliance certification requires formal audits by qualified assessors. Our tools help you prepare for audits and identify gaps, but cannot replace professional compliance review.' },
      { question: 'Are generated privacy policies legally binding?', answer: 'Generated policies provide a starting template based on your inputs. You should have any policy reviewed by legal counsel before publishing. Policies must accurately reflect your actual data practices—the generator helps you think through requirements but can\'t verify your operations.' },
      { question: 'How often should I run compliance assessments?', answer: 'We recommend quarterly self-assessments, plus additional reviews after significant changes to systems, processes, or regulations. Many frameworks require annual formal assessments—self-assessments help you stay prepared between official audits.' },
    ],
  },
  {
    name: 'Assessment',
    slug: 'assessment',
    description: 'Risk assessment, vulnerability scanning, and security evaluation tools',
    icon: 'ClipboardCheck',
    longDescription: 'Evaluate your security posture, identify risks, and benchmark your maturity against industry standards. Assessment tools help you understand where you stand and what to prioritize for improvement, with interactive evaluations covering ransomware resilience, cloud security posture, cybersecurity maturity, and business impact analysis.',
    useCases: [
      'Security teams conducting periodic posture assessments',
      'Consultants evaluating client security maturity for engagements',
      'IT managers identifying and prioritizing security investments',
      'Compliance teams mapping current controls to framework requirements',
    ],
    faqs: [
      { question: 'What types of assessments are available?', answer: 'We offer cybersecurity maturity assessments, ransomware resilience evaluations, cloud security self-assessments for AWS/Azure/GCP, business impact analysis, and risk matrix calculations aligned to NIST, ISO 27005, and FAIR frameworks.' },
      { question: 'What\'s the difference between maturity assessment and vulnerability scanning?', answer: 'Maturity assessments evaluate your overall security program—policies, processes, and capabilities—against a framework. Vulnerability scanning identifies specific technical weaknesses in systems. Both are valuable but serve different purposes.' },
      { question: 'Can I share assessment results with auditors?', answer: 'Yes. Assessment results can be exported as PDF reports suitable for sharing with auditors, executives, or clients. Results include methodology notes and timestamps documenting when assessments were conducted.' },
    ],
  },
  {
    name: 'Vendor Comparison',
    slug: 'comparison',
    description: 'Interactive selectors for picking vendors across status pages, on-call platforms, ZTNA providers, load balancers, and more',
    icon: 'Scale',
    longDescription: 'Choosing between vendors is one of the most consequential decisions an IT or security team makes — pricing, feature sets, integration depth, and operational fit all matter, and the right answer changes based on team size, budget, and existing stack. These interactive picker tools let you filter live vendor data by the criteria that actually matter to you, then rank the matches.\n\nEach picker covers a specific decision (best status page tool, best PagerDuty alternative, best ZTNA provider, etc.) with curated vendor data including pricing, key features, and honest tradeoffs. Filter by budget and required capabilities, see which vendors match all your hard requirements, and share the filtered view with your team for buying-committee alignment.',
    useCases: [
      'IT leaders building shortlists for vendor RFPs and procurement processes',
      'Engineering teams evaluating tooling for incident response, observability, and security',
      'CISOs comparing managed security service providers and SIEM platforms',
      'Startup founders selecting cost-effective alternatives to enterprise defaults',
    ],
    faqs: [
      { question: 'How often is the vendor data updated?', answer: 'Pricing and feature data is reviewed periodically and reflects publicly available information at the time of review. Always confirm specifics with vendors during procurement — packaging changes frequently, especially for enterprise tiers.' },
      { question: 'Why do some vendors rank lower even when they have more features?', answer: 'Rankings are weighted by which filters you set. A vendor that matches all your hard requirements (budget, must-have features) ranks higher than a feature-rich vendor that exceeds budget or misses a critical capability. Reset filters to see the full list.' },
      { question: 'Can I share a filtered view with my team?', answer: 'Yes. Use the "Share link" button after applying your filters to copy a URL that preserves your selections. Anyone with the link sees the same filtered ranking — useful for buying-committee alignment.' },
      { question: 'How are these different from G2 or Gartner reviews?', answer: 'G2 and Gartner aggregate reviews; these tools help you filter by requirements. Use both — start here to narrow to 2-3 vendors that match your constraints, then check reviews and analyst reports for qualitative depth.' },
    ],
  },
  {
    name: 'AI & Machine Learning',
    slug: 'ai',
    description: 'Train neural networks in your browser, count LLM tokens, calculate VRAM requirements, and estimate AI infrastructure costs',
    icon: 'Brain',
    longDescription: 'Hands-on tools for working with AI and machine learning — from learning how neural networks actually work to planning LLM deployments. Train a real neural network in your browser and watch backpropagation happen live, count tokens to estimate API costs, calculate how much VRAM a model needs to run locally, and compare inference speed across GPUs.\n\nEverything runs client-side in your browser: no accounts, no API keys, and your data never leaves your device. Whether you\'re a student learning the fundamentals, a developer planning a local LLM setup, or an engineer estimating cloud AI costs, these tools give you concrete answers fast.',
    useCases: [
      'Students and developers learning how neural networks, backpropagation, and gradient descent actually work',
      'Engineers sizing GPU hardware for local LLM deployments (VRAM and tokens-per-second estimates)',
      'Teams estimating OpenAI, Anthropic, and AWS Bedrock API costs from token counts',
      'Architects choosing AI gateways and routing infrastructure for production LLM applications',
    ],
    faqs: [
      { question: 'Do these tools send my data to AI providers?', answer: 'No. All AI & ML tools run entirely in your browser. The Neural Network Playground trains models on your device, the token counter processes text locally, and the calculators work from published model specifications. Nothing is sent to OpenAI, Anthropic, or any other provider.' },
      { question: 'Can I really train a neural network without code or a GPU?', answer: 'Yes. The Neural Network Playground implements the full training pipeline (forward pass, backpropagation, gradient descent) in JavaScript and runs it in a background thread on your CPU. Sample datasets train in under a minute. It is the same math used by TensorFlow and PyTorch, at a smaller scale.' },
      { question: 'How accurate are the VRAM and inference speed estimates?', answer: 'The calculators use published model architectures and standard formulas (parameter count × bytes per parameter + KV cache + overhead). Real-world numbers vary with your inference framework and settings, but the estimates are typically within 10-15% — accurate enough for hardware planning decisions.' },
      { question: 'Which LLMs do the token and cost tools support?', answer: 'The token counter supports GPT-4/GPT-3.5 (tiktoken), Claude, and Llama-family tokenizers. The AWS Bedrock calculator covers Claude, Llama, Titan, and other Bedrock models. The VRAM calculator works with any model on Hugging Face plus popular open models like Llama, Gemma, Qwen, and DeepSeek.' },
    ],
  },
];

export const tools: Tool[] = [
  {
    name: 'Aspect Ratio Calculator',
    description: 'Solve a missing width or height from any aspect ratio, and reduce pixel dimensions to their simplest ratio.',
    category: 'Media',
    link: '/tools/media/aspect-ratio-calculator',
    icon: '🖼️',
  },
  {
    name: 'Binomial Distribution Calculator',
    description: 'Enter n, p, and k to get P(X=k), P(X<=k), P(X>=k), the mean np, and the variance np(1-p), with a distribution chart.',
    category: 'Random',
    link: '/tools/random/binomial-distribution-calculator',
    icon: '🎲',
  },
  {
    name: 'Case Converter',
    description: 'Convert text between UPPERCASE, lowercase, Title, Sentence, camelCase, PascalCase, snake_case, kebab-case, and CONSTANT_CASE.',
    category: 'Developer',
    link: '/tools/developer/case-converter',
    icon: '⚙️',
  },
  {
    name: 'Central Limit Theorem',
    description: 'See the central limit theorem emerge: roll two dice thousands of times and watch the sum form a bell-shaped curve, even though a single die is flat.',
    category: 'Random',
    link: '/tools/random/central-limit-theorem',
    icon: '🎲',
  },
  {
    name: 'Coin Flip',
    description: 'Flip a virtual coin and get a fair, random heads or tails instantly — flip one at a time or thousands at once.',
    category: 'Random',
    link: '/tools/random/coin-flip',
    icon: '🎲',
  },
  {
    name: 'Coin Flip Simulator',
    description: 'Run thousands of coin flips and watch the heads proportion converge to 50% in real time — a live law-of-large-numbers demo.',
    category: 'Random',
    link: '/tools/random/coin-flip-simulator',
    icon: '🎲',
  },
  {
    name: 'Color Contrast Checker',
    description: 'Check the contrast ratio between text and background colors against WCAG AA and AAA accessibility thresholds.',
    category: 'Developer',
    link: '/tools/developer/color-contrast-checker',
    icon: '⚙️',
  },
  {
    name: 'Color Palette Generator',
    description: 'Build harmonious color palettes from a single base color using complementary, analogous, triadic and other color-theory schemes.',
    category: 'Developer',
    link: '/tools/developer/color-palette-generator',
    icon: '⚙️',
  },
  {
    name: 'CSS Formatter & Beautifier',
    description: 'Beautify CSS into clean, indented rules with one declaration per line, or minify it to strip whitespace and comments. Handles nested at-rules and runs in your browser.',
    category: 'Developer',
    link: '/tools/developer/css-formatter',
    icon: '⚙️',
  },
  {
    name: 'D20 Roller',
    description: 'Roll a single 20-sided die online for tabletop RPGs. Instant d20 results for attack rolls, saving throws, and ability checks, with roll history.',
    category: 'Random',
    link: '/tools/random/d20-roller',
    icon: '🎲',
  },
  {
    name: 'Dice Roller',
    description: 'Free online dice roller. Roll any dice with standard NdM notation, quick polyhedral buttons, individual rolls, totals, and roll history.',
    category: 'Random',
    link: '/tools/random/dice-roller',
    icon: '🎲',
  },
  {
    name: 'Dice Simulator',
    description: 'Roll 2d6 thousands of times and watch the sum distribution converge to the theoretical triangular curve. A live demonstration of probability and the central limit theorem.',
    category: 'Random',
    link: '/tools/random/dice-simulator',
    icon: '🎲',
  },
  {
    name: 'DnD Dice Roller',
    description: 'Roll the full Dungeons & Dragons polyhedral dice set online (d4 through d100), plus d20 ability checks with advantage and disadvantage.',
    category: 'Random',
    link: '/tools/random/dnd-dice-roller',
    icon: '🎲',
  },
  {
    name: 'Email Verifier',
    description: 'Verify whether an email address is real and can receive mail. Checks syntax, MX records, disposable and role addresses, and probes the mailbox itself — then tells you if it is deliverable, risky, or undeliverable.',
    category: 'Developer',
    link: '/tools/developer/email-verifier',
    icon: '⚙️',
  },
  {
    name: 'Expected Value Calculator',
    description: 'Add outcome values and their probabilities to compute E[X] = the sum of value times probability, with a per-term breakdown.',
    category: 'Random',
    link: '/tools/random/expected-value-calculator',
    icon: '🎲',
  },
  {
    name: 'Hex to RGB Converter',
    description: 'Convert a hexadecimal color code to its red, green and blue values for use in CSS rgb() and rgba().',
    category: 'Developer',
    link: '/tools/developer/hex-to-rgb',
    icon: '⚙️',
  },
  {
    name: 'HTML Formatter & Beautifier',
    description: 'Beautify and pretty-print messy HTML with clean indentation, or minify it to strip whitespace and comments. Runs entirely in your browser.',
    category: 'Developer',
    link: '/tools/developer/html-formatter',
    icon: '⚙️',
  },
  {
    name: 'JavaScript Formatter',
    description: 'Beautify and format messy or minified JavaScript with clean indentation, consistent spacing, and one statement per line. Runs entirely in your browser.',
    category: 'Developer',
    link: '/tools/developer/javascript-formatter',
    icon: '⚙️',
  },
  {
    name: 'JavaScript Minifier',
    description: 'Minify and compress JavaScript to reduce file size for production. Strips comments and whitespace and safely renames local variables using Terser, in your browser.',
    category: 'Developer',
    link: '/tools/developer/javascript-minifier',
    icon: '⚙️',
  },
  {
    name: 'Law of Large Numbers',
    description: 'Watch the law of large numbers in action: flip a coin thousands of times and see the running proportion of heads converge to 0.5 in real time.',
    category: 'Random',
    link: '/tools/random/law-of-large-numbers',
    icon: '🎲',
  },
  {
    name: 'Magic 8 Ball',
    description: 'Ask the online Magic 8 Ball a yes-or-no question and shake for one of the 20 classic answers.',
    category: 'Random',
    link: '/tools/random/magic-8-ball',
    icon: '🎲',
  },
  {
    name: 'Markdown Table Generator',
    description: 'Build GitHub-Flavored-Markdown tables in a visual grid: set rows and columns, fill in cells, choose per-column alignment, and copy clean Markdown syntax.',
    category: 'Developer',
    link: '/tools/developer/markdown-table-generator',
    icon: '⚙️',
  },
  {
    name: 'Normal Distribution Calculator',
    description: 'Enter the mean, standard deviation, and an x value to get the z-score, density, and probabilities P(X<=x), P(X>=x), and a range.',
    category: 'Random',
    link: '/tools/random/normal-distribution-calculator',
    icon: '🎲',
  },
  {
    name: 'Random Letter Generator',
    description: 'Generate one or many random letters A–Z, with upper/lowercase and a vowels-only option — built for word games and teaching.',
    category: 'Random',
    link: '/tools/random/random-letter-generator',
    icon: '🎲',
  },
  {
    name: 'Random Name Picker',
    description: 'Paste a list of names and pick one at random, fairly. A free random name picker for classrooms, prize draws, and giveaways — spin, select a winner, repeat.',
    category: 'Random',
    link: '/tools/random/random-name-picker',
    icon: '🎲',
  },
  {
    name: 'Random Number Between 1 and 100',
    description: 'Pick a random number between 1 and 100 instantly — great for games, giveaways, and quick decisions.',
    category: 'Random',
    link: '/tools/random/random-number-between-1-and-100',
    icon: '🎲',
  },
  {
    name: 'Random Number Generator',
    description: 'Generate random numbers in any range — multiple at once, no-repeat, decimals, negatives, Gaussian, dice notation, and binary/octal/hex output.',
    category: 'Random',
    link: '/tools/random/random-number-generator',
    icon: '🎲',
  },
  {
    name: 'Random Team Generator',
    description: 'Paste a list of names, choose how many teams, and split everyone into fair, evenly-sized random teams instantly. Free team picker for class, sports, and work.',
    category: 'Random',
    link: '/tools/random/random-team-generator',
    icon: '🎲',
  },
  {
    name: 'RGB to Hex Converter',
    description: 'Convert red, green and blue values into a six-digit hexadecimal color code for HTML and CSS.',
    category: 'Developer',
    link: '/tools/developer/rgb-to-hex',
    icon: '⚙️',
  },
  {
    name: 'Spinner Wheel / Random Picker',
    description: 'A free, customizable spinning wheel. Add your own entries, spin a colorful wheel, and let chance pick a random winner — right in your browser.',
    category: 'Random',
    link: '/tools/random/spinner-wheel',
    icon: '🎲',
  },
  {
    name: 'Standard Deviation Calculator',
    description: 'Paste a list of numbers to get the mean, variance, and both population and sample standard deviation — with every step shown.',
    category: 'Random',
    link: '/tools/random/standard-deviation-calculator',
    icon: '🎲',
  },
  {
    name: 'Wheel of Names',
    description: 'Spin a free wheel of names to draw a random winner. Paste your class roster or entrant list, spin, and remove winners as you go — perfect for classrooms and raffles.',
    category: 'Random',
    link: '/tools/random/wheel-of-names',
    icon: '🎲',
  },
  {
    name: 'XML Formatter',
    description: 'Beautify, indent, and minify XML with live validation — all in your browser.',
    category: 'Developer',
    link: '/tools/developer/xml-formatter',
    icon: '⚙️',
  },
  {
    name: 'YAML Formatter & Validator',
    description: 'Validate YAML and reformat it with consistent indentation and normalized quoting. Reports parse errors with the exact line and column. Runs in your browser.',
    category: 'Developer',
    link: '/tools/developer/yaml-formatter',
    icon: '⚙️',
  },
  {
    name: 'Yes or No Wheel',
    description: 'A free yes or no wheel for fast decisions. Spin for an instant answer, with an optional Maybe slice for the tougher calls. Fair, random, and works on any device.',
    category: 'Random',
    link: '/tools/random/yes-or-no-wheel',
    icon: '🎲',
  },
  // Security Tools
  {
    name: 'Free Breach Check Tool',
    description: 'Check if an email address or password has appeared in known data breaches. See which breaches, what data was exposed, and when.',
    category: 'Security',
    link: '/tools/breach-checker',
    icon: '🔓',
  },
{
    name: 'Password Strength Checker',
    description: 'Check how strong your password is with entropy analysis, crack time estimates, and breach database checks via Have I Been Pwned. 100% client-side.',
    category: 'Security',
    link: '/tools/security/password-strength-checker',
    icon: '🔐',
  },
  {
    name: 'Password Generator',
    description: 'Generate secure random passwords with customizable options',
    category: 'Security',
    link: '/tools/security/secure-password-generator',
    icon: '🔑',
  },
  {
    name: 'CVE Vulnerability Search & Timeline',
    description: 'Search CVEs, visualize vendor trends, analyze response times, and calculate CVSS scores',
    category: 'Security',
    link: '/tools/security/cve-lookup',
    icon: '🔍',
  },
  {
    name: 'CWE Lookup Tool',
    description: 'Look up Common Weakness Enumeration entries from MITRE with detailed mitigations and Top 25 list',
    category: 'Security',
    link: '/tools/security/cwe-lookup',
    icon: '🛡️',
  },
  {
    name: 'CVSS v3.1 Calculator',
    description: 'Calculate CVSS v3.1 vulnerability scores with Base, Temporal, and Environmental metrics. Generate vector strings and severity ratings.',
    category: 'Security',
    link: '/tools/security/cvss-calculator',
    icon: '📊',
  },
  {
    name: 'SystemLens',
    description: 'Desktop app for filesystem analysis and security auditing with SSH scanning (macOS, Windows, Linux)',
    category: 'Security',
    link: '/tools/security/systemlens',
    icon: '🖥️',
  },
  {
    name: 'Hash Generator - MD5, SHA-256, SHA-512',
    description: 'Generate MD5, SHA-1, SHA-256, and SHA-512 hashes for text and files. Check malware hashes with VirusTotal integration.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/hash-generator',
    icon: '🔐',
  },
  {
    name: 'Data Broker Opt-Out Checklist',
    description: 'Official opt-out links for 30 people-search and data broker sites, with what each one asks for and progress saved in your browser',
    category: 'Security',
    link: '/tools/security/data-broker-opt-out-checklist',
    icon: '🕵️',
  },
  {
    name: 'MD5 Generator',
    description: 'Generate an MD5 hash from text or a file in your browser, and verify a download against its published checksum.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/md5-generator',
    icon: '#️⃣',
  },
  {
    name: 'Base64 Encoder/Decoder',
    description: 'Encode and decode Base64, Hex, Binary, and other formats with file support',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/base64-encoder-decoder',
    icon: '🔣',
  },
  {
    name: 'JWT Decoder & Validator',
    description: 'Decode and inspect JWT tokens instantly. View header, payload, and verify signatures with security validation.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/jwt-decoder',
    icon: '🎫',
  },
  {
    name: 'OAuth/OIDC Debugger',
    description: 'Debug OAuth 2.0 and OpenID Connect flows with JWT decoder, PKCE generator, flow tester, and error troubleshooting',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/oauth-oidc-debugger',
    icon: '🔓',
  },
  {
    name: 'ROT13 Decoder & Caesar Cipher Solver',
    description: 'Decode ROT13 instantly or crack Caesar ciphers with brute force solver. Encode with custom shifts (1-25). Free online decoder.',
    category: 'Security',
    link: '/tools/security/rot13-cipher',
    icon: '🔄',
  },
  {
    name: 'X.509 Certificate Decoder',
    description: 'Decode and analyze SSL/TLS certificates with security validation',
    category: 'Security',
    link: '/tools/security/x509-decoder',
    icon: '📜',
    embed: 'hero',
  },
  {
    name: 'Certificate CSR Generator & Format Converter',
    description: 'Generate CSRs, convert between PEM/DER/PFX/P7B formats, and verify certificate chains - 100% client-side',
    category: 'Security',
    link: '/tools/security/certificate-csr-generator',
    icon: '🔐',
  },
  {
    name: 'Certificate Chain Builder',
    description: 'Build complete SSL/TLS certificate chains automatically. Upload your certificate and fetch missing intermediate CA certs.',
    category: 'Security',
    link: '/tools/security/certificate-chain-builder',
    icon: '🔗',
  },
  {
    name: 'SSH Key Generator - Ed25519, RSA, ECDSA',
    description: 'Generate SSH key pairs (Ed25519, RSA, ECDSA) with fingerprints and randomart visualization. 100% client-side — keys never leave your browser.',
    category: 'Security',
    link: '/tools/security/ssh-key-generator',
    icon: '🔑',
  },
  {
    name: 'PGP Key Generator',
    description: 'Generate PGP/GPG key pairs (Curve25519, RSA, NIST curves) with fingerprints and QR codes - 100% client-side',
    category: 'Security',
    link: '/tools/security/pgp-key-generator',
    icon: '🔐',
  },
  {
    name: 'XOR Decoder & Brute Force Tool',
    description: 'Free XOR encryption and decryption online. Encrypt text, decode XOR ciphertext, brute force cryptanalysis',
    category: 'Security',
    link: '/tools/security/xor-cipher',
    icon: '⚡',
  },
  {
    name: 'AES Encryption Tool',
    description: 'Encrypt and decrypt text or files with AES-256-GCM, AES-CBC, or AES-CTR. Runs in your browser.',
    category: 'Security',
    link: '/tools/security/aes-encryption-tool',
    icon: '🔒',
  },
  {
    name: 'Malware Deobfuscator',
    description: 'Multi-technique deobfuscation tool for malware analysis with auto-detection and chaining',
    category: 'Security',
    link: '/tools/security/malware-deobfuscator',
    icon: '🔬',
  },
  {
    name: 'URL Defanger',
    description: 'Defang and refang URLs, IPs, and domains for threat intelligence',
    category: 'Security',
    link: '/tools/security/url-defanger',
    icon: '🔗',
  },
  {
    name: 'Machine Code Disassembler',
    description: 'Disassemble x86/ARM/RISC-V machine code with assembler, shellcode detection, call graphs, and performance analysis',
    category: 'Security',
    link: '/tools/security/machine-code-disassembler',
    icon: '⚙️',
  },
  {
    name: 'WebAssembly Debugger',
    description: 'Inspect, disassemble, and safely debug WebAssembly instruction by instruction with breakpoints, memory watchpoints, and reversible time-travel traces',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/webassembly-debugger',
    icon: '🐛',
    embed: 'card',
  },
  {
    name: 'Binary Lab',
    description: 'Keep local binary artifacts, hashes, notes, findings, and tool handoffs together in a private browser workspace',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/binary-lab',
    icon: '🧪',
    embed: 'card',
  },
  {
    name: 'Executable File Inspector',
    description: 'Inspect PE, ELF, Mach-O, universal binaries, and Unix archives with bounded parsers and security heuristics',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/executable-file-inspector',
    icon: '🔎',
    embed: 'card',
  },
  {
    name: 'Assembler Playground',
    description: 'Assemble a deterministic Intel x86/x64 subset into exact machine-code bytes and instruction boundaries',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/assembler-playground',
    icon: '🧩',
    embed: 'card',
  },
  {
    name: 'YARA-X Rule Workbench',
    description: 'Author and compile YARA-X rules, scan artifacts locally, inspect match offsets, and save evidence',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/yara-rule-workbench',
    icon: '🎯',
    embed: 'card',
  },
  {
    name: 'Binary Diff & Patch',
    description: 'Compare binary artifacts and create reversible copy/insert patches locked to verified SHA-256 identities',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/binary-diff-patch',
    icon: '↔️',
    embed: 'card',
  },
  {
    name: 'Shellcode Emulator',
    description: 'Model a bounded x86/x64 instruction subset with reversible steps and blocked syscall boundaries',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/shellcode-emulator',
    icon: '🧠',
    embed: 'card',
  },
  {
    name: 'Network Investigation Lab',
    description: 'Normalize a domain, IP, CIDR, or URL, compare DNS snapshots, save findings locally, and continue across network investigation tools',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/network-investigation-lab',
    icon: '📡',
    embed: 'card',
    howToSteps: [
      { name: 'Identify the target', text: 'Enter a domain, IP address, CIDR range, or HTTP URL to normalize its safe investigation context.' },
      { name: 'Compare captured state', text: 'Paste before-and-after DNS answer snapshots to isolate record and TTL changes without issuing a scan.' },
      { name: 'Save and continue', text: 'Save a local evidence workspace and carry its opaque session into focused DNS, TLS, packet, routing, and firewall tools.' },
    ],
  },
  {
    name: 'Identity Protocol Lab',
    description: 'Inspect JWT, SAML, OIDC, JWKS, WebAuthn, and SCIM artifacts locally, generate PKCE, and track protocol findings',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/identity-protocol-lab',
    icon: '🪪',
    embed: 'card',
    howToSteps: [
      { name: 'Paste an artifact', text: 'Choose a protocol or let the lab identify a bounded token, assertion, discovery document, key set, or schema payload.' },
      { name: 'Review structure and warnings', text: 'Inspect normalized facts and distinguish parsing warnings from cryptographic verification and issuer trust.' },
      { name: 'Continue safely', text: 'Save only the normalized result and hand off to focused JWT, OAuth, federation, and passkey tools.' },
    ],
  },
  {
    name: 'Certificate Trust Lab',
    description: 'Inventory PEM and DER artifacts, calculate fingerprints, inspect X.509 structure, and coordinate certificate trust checks',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/certificate-trust-lab',
    icon: '🔐',
    embed: 'card',
    howToSteps: [
      { name: 'Load certificate material', text: 'Paste bounded PEM data or choose a local certificate file; the raw material stays in the browser.' },
      { name: 'Inspect the inventory', text: 'Review block types, byte sizes, SHA-256 fingerprints, X.509 identity, validity, key, and signature details.' },
      { name: 'Validate the trust path', text: 'Carry the local workspace into focused chain, CSR, transparency, revocation, and live TLS tools.' },
    ],
  },
  {
    name: 'Reliability Engineering Lab',
    description: 'Model SLO budgets, architecture availability, downtime impact, and capacity growth in a reusable local service workspace',
    category: 'Planning',
    secondary_categories: ['Developer'],
    link: '/tools/planning/reliability-engineering-lab',
    icon: '💓',
    embed: 'card',
    howToSteps: [
      { name: 'Describe the service', text: 'Set the objective, observed availability, traffic, impact, capacity, growth, and component topology.' },
      { name: 'Review the model', text: 'Calculate time and request error budgets, modeled system availability, downtime cost, and months to capacity.' },
      { name: 'Plan the response', text: 'Save assumptions and findings, then continue into recovery, dependency, impact, and reliability calculators.' },
    ],
  },
  {
    name: 'Security Program Workspace',
    description: 'Track baseline controls, owners, evidence, framework references, risks, and prioritized remediation in one local workspace',
    category: 'Compliance',
    secondary_categories: ['Assessment', 'Planning', 'Security'],
    link: '/tools/compliance/security-program-workspace',
    icon: '🛡️',
    embed: 'card',
    howToSteps: [
      { name: 'Assess the baseline', text: 'Record status, owner, target date, and evidence for 12 foundational security capabilities.' },
      { name: 'Capture risks', text: 'Score inherent likelihood and impact, assign owners, and document planned treatments.' },
      { name: 'Prioritize and export', text: 'Review weighted coverage and remediation priorities, then save a local evidence snapshot for continued planning.' },
    ],
  },
  {
    name: 'Local AI Deployment Lab',
    description: 'Plan a private LLM deployment from model and hardware fit through estimated speed and reviewable Ollama or llama.cpp output',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning', 'Planning'],
    link: '/tools/developer/local-ai-deployment-lab',
    icon: '🧠',
    embed: 'card',
    howToSteps: [
      { name: 'Choose model and hardware', text: 'Select an open model, quantization, context size, accelerator, GPU count, and runtime.' },
      { name: 'Check estimated fit', text: 'Review weights, KV cache, runtime overhead, usable memory, utilization, and single-user generation speed.' },
      { name: 'Review the starter configuration', text: 'Copy a transparent Ollama or llama.cpp starting point, then validate it with focused calculators and measured benchmarks.' },
    ],
  },
  {
    name: 'MITRE ATT&CK Browser',
    description: 'Explore MITRE ATT&CK tactics and techniques for threat hunting',
    category: 'Security',
    link: '/tools/security/mitre-attack',
    icon: '🎯',
  },
  {
    name: 'Nmap Command Builder',
    description: 'Visual Nmap command generator with preset templates and intelligent results parser',
    category: 'Security',
    link: '/tools/security/nmap-command-builder',
    icon: '🔍',
  },
  {
    name: 'Metasploit Command Builder',
    description: 'Visual Metasploit command generator with module presets, payload selector, and console command reference',
    category: 'Security',
    link: '/tools/security/metasploit-command-builder',
    icon: '⚔️',
  },
  {
    name: 'Domain Spoofing Detector',
    description: 'Detect homograph attacks and typosquatting. Analyze suspicious domains or generate protective variants',
    category: 'Security',
    link: '/tools/security/domain-spoofing-detector',
    icon: '🕵️',
  },
  {
    name: 'IP Score & Risk Checker',
    description: 'Check IP reputation, geolocation, and threat intelligence with fraud detection',
    category: 'Security',
    link: '/tools/security/ip-risk-checker',
    icon: '🌐',
  },
  {
    name: 'Certificate Transparency Lookup',
    description: 'Search Certificate Transparency logs for SSL/TLS certificates and subdomains',
    category: 'Security',
    link: '/tools/security/certificate-transparency-lookup',
    icon: '🔐',
  },
  {
    name: 'Domain Risk Scanner',
    description: 'Comprehensive domain security scan: SSL/TLS, security headers, email authentication, DNS health, HTTPS enforcement, breach history, domain reputation, and privacy compliance — all in one report with a weighted risk score',
    category: 'Security',
    link: '/tools/security/domain-risk-scanner',
    icon: '🔎',
  },
  {
    name: 'SSL Checker',
    description: 'Run quick or deep SSL scans to verify certificate chains, protocol support, and common configuration issues',
    category: 'Security',
    link: '/tools/security/ssl-checker',
    icon: '🔒',
  },
  {
    name: 'Incident Response Playbook & Runbook Generator',
    description: 'Create customized IR playbooks aligned with NIST SP 800-61 for ransomware, data breaches, DDoS, supply chain compromise, and operational runbooks. Includes evidence preservation checklists, compliance guidance (GDPR, HIPAA, PCI-DSS), team roles, and export to PDF/Markdown',
    category: 'Security',
    link: '/tools/security/incident-response-playbook-generator',
    icon: '📋',
  },
  {
    name: 'Email Template Generator',
    description: 'Generate professional email templates for incident response, disaster recovery tests, maintenance notifications, security alerts, and stakeholder communications',
    category: 'Security',
    link: '/tools/security/email-template-generator',
    icon: '📧',
  },
  {
    name: 'Email Deliverability Checker',
    description: 'Spam checker for your domain: tests MX, SPF (syntax and the 10-lookup limit), DKIM, DMARC, BIMI, MTA-STS and reverse DNS in one report, with a fix for every failure.',
    category: 'Security',
    link: '/tools/security/email-deliverability-checker',
    icon: '📬',
  },
  {
    name: 'Email Security Gateway Checker',
    description: 'Identify which secure email gateway protects a domain and check MTA-STS, TLS-RPT, DANE, DNSSEC, and BIMI. Passive DNS only — no probing.',
    category: 'Security',
    link: '/tools/security/email-security-checker',
    icon: '🛡️',
  },
  {
    name: 'Email Authentication Validator',
    description: 'Validate SPF, DKIM, and DMARC records with security grading and recommendations',
    category: 'Security',
    link: '/tools/security/email-auth-validator',
    icon: '📧',
  },
  {
    name: 'Email Validator & MX Checker',
    description: 'Validate email syntax, MX records, disposable domains, and provider risk scoring',
    category: 'Security',
    link: '/tools/security/email-validator-mx-checker',
    icon: '📬',
  },
  {
    name: 'Email Header Analyzer',
    description: 'Analyze email headers for SPF/DKIM/DMARC validation, routing, and security threats',
    category: 'Security',
    link: '/tools/security/email-header-analyzer',
    icon: '📨',
    embed: 'hero',
  },
  {
    name: 'Phishing Risk Assessment',
    description: 'Score your organisation against 21 phishing and business email compromise controls across technical filtering, identity, awareness, response, and financial governance.',
    category: 'Security',
    secondary_categories: ['Assessment'],
    link: '/tools/security/phishing-risk-assessment',
    icon: '🎣',
    embed: 'card',
  },
  {
    name: 'Phishing Email Analyzer',
    description: 'Paste a suspicious email and get an instant phishing risk score — checks for spoofed senders, deceptive links, lookalike domains, urgency tactics, and dangerous attachments. 100% private: nothing is uploaded, with an optional in-browser AI explanation.',
    category: 'Security',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/security/phishing-email-analyzer',
    icon: '🎣',
    embed: 'card',
    howToSteps: [
      { name: 'Paste the email', text: 'Copy the full suspicious email (headers and body) and paste it in — it is analyzed entirely in your browser, nothing is uploaded.' },
      { name: 'Read the risk score', text: 'See a phishing risk score and a breakdown of every warning sign: link mismatches, lookalike domains, urgency language, impersonation, and more.' },
      { name: 'Get a plain-English explanation', text: 'Optionally run a small AI model locally to explain what the flags mean and what to do.' },
    ],
  },
  {
    name: 'PII Redactor',
    description: 'Detect and redact personal and sensitive data — emails, SSNs, credit cards, phone numbers, IPs, API keys — from any text or logs before you share them. Runs 100% in your browser; nothing is uploaded.',
    category: 'Security',
    secondary_categories: ['Developer', 'AI & Machine Learning'],
    link: '/tools/security/pii-redactor',
    icon: '🕵️',
    embed: 'card',
    howToSteps: [
      { name: 'Paste your text', text: 'Drop in logs, documents, or any text containing sensitive data — it never leaves your browser.' },
      { name: 'Review detected PII', text: 'See every email, SSN, credit card, phone, IP, and key found, grouped by type. Toggle which types to redact.' },
      { name: 'Copy or download', text: 'Get the redacted text with your chosen masking style. An optional local AI can also flag names and addresses to review.' },
    ],
  },
  {
    name: 'Private AI Summarizer',
    description: 'Summarize any text with AI that runs entirely in your browser — paste an article, contract, or report and get bullet points or a TL;DR. Unlike cloud summarizers, your text is never uploaded.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/private-ai-summarizer',
    icon: '📝',
    embed: 'card',
    howToSteps: [
      { name: 'Paste your text', text: 'Drop in the text you want summarized. It stays in your browser — nothing is sent to any server.' },
      { name: 'Pick a style', text: 'Choose bullet points, a paragraph, a one-sentence TL;DR, or key takeaways, and a length.' },
      { name: 'Summarize privately', text: 'A small AI model runs locally via WebGPU and streams the summary back. First run downloads the model (cached after).' },
    ],
  },
  {
    name: 'Local AI Chat',
    description: 'Chat with an AI model that runs entirely in your browser — free, no signup, no API key. Pick a model like Llama 3.2 or Qwen 2.5, load your own GGUF file, and conversations never leave your device.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/local-ai-chat',
    icon: '💬',
    embed: 'card',
    howToSteps: [
      { name: 'Pick a model', text: 'Choose a recommended model for your hardware, paste a HuggingFace MLC repo, or load a .gguf file from your computer. Models download once and are cached for every local-AI tool on this site.' },
      { name: 'Start chatting', text: 'Multi-turn conversation with streaming responses, a live tokens-per-second counter, and selectable AI personalities. Works offline after the model downloads.' },
      { name: 'Keep it private', text: 'The model runs on your GPU via WebGPU (or CPU for GGUF files). Conversations are stored only in your browser and can be exported as Markdown.' },
    ],
  },
  {
    name: 'Security Headers Analyzer',
    description: 'Test HTTP security headers and get security scores with actionable recommendations',
    category: 'Security',
    link: '/tools/security/security-headers-analyzer',
    icon: '🛡️',
  },
  {
    name: 'File Magic Bytes Checker',
    description: 'Identify true file types by analyzing magic numbers and detect extension spoofing',
    category: 'Security',
    link: '/tools/security/file-magic-number-checker',
    icon: '🔬',
  },
  {
    name: 'IOC Extractor',
    description: 'Extract indicators of compromise (IPs, domains, URLs, hashes) from threat intel',
    category: 'Security',
    link: '/tools/security/ioc-extractor',
    icon: '🔍',
  },
  {
    name: 'Threat Intelligence Feed Aggregator',
    description: 'Aggregate IOCs from multiple feeds, deduplicate indicators, and export collections for investigations',
    category: 'Security',
    link: '/tools/security/threat-intel-aggregator',
    icon: '🛰️',
  },
  {
    name: 'Hash Lookup',
    description: 'Look up hash values to identify known malware and file signatures',
    category: 'Security',
    link: '/tools/security/hash-lookup',
    icon: '🔎',
  },
  {
    name: 'Hash Type Identifier',
    description: 'Identify the algorithm behind a hash and get the hashcat mode and John the Ripper format. Recognises MD5, SHA family, bcrypt, NTLM, NetNTLMv2, and Unix crypt formats. 100% client-side.',
    category: 'Security',
    link: '/tools/security/hash-type-identifier',
    icon: '🔡',
  },
  {
    name: 'robots.txt Analyzer',
    description: 'Parse and analyze robots.txt files with path testing and security checks',
    category: 'Security',
    link: '/tools/security/robots-txt-analyzer',
    icon: '🤖',
  },
  {
    name: 'Cookie Analyzer',
    description: 'Analyze HTTP cookies for security issues and privacy concerns',
    category: 'Security',
    secondary_categories: ['Compliance'],
    link: '/tools/security/cookie-analyzer',
    icon: '🍪',
    embed: 'hero',
  },
  {
    name: 'DMARC Record Generator',
    description: 'Generate DMARC records with step-by-step wizard, preset templates, and deployment roadmap',
    category: 'Security',
    secondary_categories: ['Compliance'],
    link: '/tools/security/dmarc-generator',
    icon: '📧',
  },
  {
    name: 'GDPR Compliance Checker',
    description: 'Scan websites for GDPR compliance issues including cookie consent, privacy policy, and third-party trackers',
    category: 'Compliance',
    link: '/tools/compliance/gdpr-checker',
    icon: '🔒',
  },
  {
    name: 'Privacy Policy Generator',
    description: 'Generate customized privacy policies for websites and apps with GDPR, CCPA, and COPPA compliance sections',
    category: 'Compliance',
    link: '/tools/compliance/privacy-policy-generator',
    icon: '📜',
  },
  {
    name: 'Security Policy Generator',
    description: 'Generate customized security policies including Acceptable Use, Password, Incident Response, Access Control, Remote Work, and Data Classification policies',
    category: 'Compliance',
    link: '/tools/compliance/security-policy-generator',
    icon: '📋',
  },
  {
    name: 'CSP Policy Generator',
    description: 'Generate Content Security Policy headers with nonce/hash support and validation',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/csp-generator',
    icon: '🛡️',
  },
  {
    name: 'SPF Record Generator',
    description: 'Generate SPF records with automatic DNS lookup counter and optimization',
    category: 'Security',
    secondary_categories: ['Compliance'],
    link: '/tools/security/spf-generator',
    icon: '📧',
  },
  {
    name: 'Email Setup Wizard',
    description: 'Set up email authentication with guided SPF, DKIM, and DMARC configuration for your domain',
    category: 'Security',
    link: '/tools/email-setup-wizard',
    embed: 'card',
    icon: '✉️',
  },
  {
    name: 'CORS Policy Analyzer',
    description: 'Detect and analyze CORS misconfigurations, security vulnerabilities, and policy issues',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/cors-policy-analyzer',
    icon: '🌐',
  },
  {
    name: 'String Extractor',
    description: 'Extract ASCII and Unicode strings from binary files for malware analysis with IOC pattern detection',
    category: 'Security',
    link: '/tools/security/string-extractor',
    icon: '🔤',
  },
  {
    name: 'Entropy Analyzer',
    description: 'Calculate Shannon entropy to detect packed, encrypted, or obfuscated malware in binary files',
    category: 'Security',
    link: '/tools/security/entropy-analyzer',
    icon: '📊',
  },
  {
    name: 'Hex Editor',
    description: 'Binary file viewer and editor with hex/ASCII display, search, bookmarks, and byte editing',
    category: 'Security',
    link: '/tools/security/hex-editor',
    icon: '🔍',
  },
  {
    name: 'Secrets Scanner Simulator',
    description: 'Detect exposed API keys, tokens, private keys, and database credentials in code. 100% client-side scanning.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/secrets-scanner',
    icon: '🔎',
  },
  {
    name: 'SBOM Generator',
    description: 'Generate Software Bill of Materials (SBOM) in CycloneDX and SPDX formats for supply chain security and EO 14028 compliance',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/sbom-generator',
    icon: '📦',
  },
  {
    name: 'NIST CSF Mapper',
    description: 'Map NIST Cybersecurity Framework 2.0 controls to CIS Controls v8, ISO 27001:2022, and SOC 2 Trust Services Criteria',
    category: 'Security',
    link: '/tools/security/nist-csf-mapper',
    icon: '🗺️',
  },
  {
    name: 'Kubernetes Manifest Validator',
    description: 'Validate K8s manifests against CIS Kubernetes Benchmark security checks. Detect privileged containers, missing resource limits, and misconfigurations.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/security/kubernetes-manifest-validator',
    icon: '☸️',
  },
  {
    name: 'SIEM Query Builder',
    description: 'Build detection queries for Splunk SPL, Elastic KQL, and Microsoft Sentinel. Includes presets for authentication, network, malware, and threat hunting with MITRE ATT&CK mappings.',
    category: 'Security',
    link: '/tools/security/siem-query-builder',
    embed: 'card',
    icon: '🔎',
  },
  {
    name: 'Log Pattern Library',
    description: 'Browsable reference of common log file locations and error patterns for nginx, Apache, MySQL, PostgreSQL, Redis, sshd, Docker, systemd and more — with validated regex and copy-ready monitoring configs.',
    category: 'Security',
    secondary_categories: ['Developer'],
    link: '/tools/log-patterns',
    embed: 'card',
    icon: '📜',
  },
  {
    name: 'Windows Event ID Lookup',
    description: 'Search common Windows Event Log IDs by number or keyword, filter by channel and severity, and generate a ready-to-paste log-monitoring config block.',
    category: 'Security',
    link: '/tools/windows-event-id-lookup',
    icon: '🪟',
  },
  // Network Tools
  {
    name: 'VoIP Bandwidth Calculator',
    description: 'Per-call and total VoIP bandwidth for G.711, G.729, G.722 and Opus with Ethernet, VPN and cRTP overhead, plus an estimated MOS from your latency, jitter and loss',
    category: 'Network',
    link: '/tools/network/voip-bandwidth-calculator',
    icon: '📞',
  },
  {
    name: 'Subnet Calculator',
    description: 'Calculate subnet masks, network ranges, and IP addressing',
    category: 'Network',
    link: '/tools/network/subnet-calculator',
    icon: '🌐',
  },
  {
    name: 'PCAP Analyzer',
    description: 'Analyze a .pcap or .pcapng packet capture in your browser. Get protocol breakdowns, top talkers, conversations, ports, and TCP flags — no capture data is uploaded.',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/pcap-analyzer',
    icon: '🦈',
  },
  {
    name: 'DNS Lookup',
    description: 'Query DNS records with email security analysis and health scoring',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/dns-lookup',
    icon: '🔍',
    embed: 'hero',
  },
  {
    name: 'Port Reference',
    description: 'Comprehensive network port reference database with search - 5,900+ ports',
    category: 'Network',
    link: '/tools/network/port-reference',
    icon: '📚',
    embed: 'hero',
  },
  {
    name: 'URL Expander',
    description: 'Expand shortened URLs safely and analyze redirect chains with security checks',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/url-expander',
    icon: '🔗',
  },
  {
    name: 'Redirect Chain Checker',
    description: 'Trace HTTP redirect chains and analyze response headers for debugging',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/redirect-chain-checker',
    icon: '🔄',
  },
  {
    name: 'IP Geolocation Lookup',
    description: 'Look up geographic location, ISP, and threat intelligence for IP addresses',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/ip-geolocation-lookup',
    icon: '🌍',
  },
  {
    name: 'WHOIS Lookup',
    description: 'Query domain registration information, expiration dates, and nameservers',
    category: 'Network',
    secondary_categories: ['Security'],
    link: '/tools/network/whois-lookup',
    icon: '📝',
  },
  {
    name: 'BGP AS Lookup',
    description: 'Look up BGP Autonomous System information by ASN or find which AS an IP address belongs to',
    category: 'Network',
    link: '/tools/network/bgp-as-lookup',
    icon: '🌐',
  },
  {
    name: 'MAC Address Lookup',
    description: 'Advanced MAC address analyzer with instant OUI lookup, batch processing, vendor search, duplicate detection, binary visualization, EUI-64 conversion, and lookup history',
    category: 'Network',
    link: '/tools/network/mac-address-lookup',
    icon: '🔌',
    embed: 'hero',
  },
  {
    name: 'SNMP OID Lookup & snmpwalk Builder',
    description: 'Look up SNMP OIDs by number or name across MIB-2, IF-MIB, HOST-RESOURCES, LLDP, CDP, BRIDGE and Cisco, Juniper, Aruba, Fortinet, Palo Alto, MikroTik, Ubiquiti and APC MIBs, then build snmpwalk, snmpget, snmpbulkwalk and snmptable commands for SNMP v1, v2c and v3',
    category: 'Network',
    link: '/tools/network/snmp-oid-lookup',
    icon: '📡',
  },
  {
    name: 'What Is My IP',
    description: 'See your public IP (IPv4 & IPv6), approximate location, ISP, user agent, browser, OS, device, screen resolution and connection — detected live, nothing stored',
    category: 'Network',
    link: '/tools/network/what-is-my-ip',
    icon: '🌍',
  },
  {
    name: 'Network Latency Calculator',
    description: 'Calculate network latency, ping distance, and data transfer times between locations',
    category: 'Network',
    link: '/tools/network/network-latency-calculator',
    icon: '⚡',
  },
  {
    name: 'TCP Window Size Calculator',
    description: 'Calculate optimal TCP window sizes, BDP, and buffer configurations for WAN optimization. Generate OS-specific tuning commands for Linux, Windows, and macOS to maximize network throughput.',
    category: 'Network',
    link: '/tools/network/tcp-window-size-calculator',
    icon: '📊',
  },
  {
    name: 'Wireless Security Architecture Planner',
    description: 'Plan wireless network deployments with proper security protocols, antenna selection, 802.11 standards comparison, and security configuration checklists. CISSP Domain 4 aligned.',
    category: 'Network',
    link: '/tools/network/wireless-security-planner',
    embed: 'card',
    icon: '📡',
  },
  {
    name: 'IPv4 to IPv6 Converter',
    description: 'Convert between IPv4 and IPv6 address formats instantly. Supports IPv4-mapped, 6to4, Teredo, and format conversion.',
    category: 'Network',
    link: '/tools/network/ipv4-ipv6-converter',
    icon: '🔄',
  },
  {
    name: 'Firewall Rule Logic Simulator',
    description: 'Build firewall rulesets, test packets against rules, compare stateful vs stateless filtering, and analyze rule ordering. CISSP Domain 4 study tool.',
    category: 'Network',
    link: '/tools/network/firewall-rule-simulator',
    embed: 'card',
    icon: '🛡️',
  },
  // Developer Tools
  {
    name: 'Terraform Plan Explainer',
    description: 'Analyze Terraform plans for security risks, blast radius, and dependencies. Reduce production incidents by understanding infrastructure changes before applying them.',
    category: 'Developer',
    link: '/tools/developer/terraform-plan-explainer',
    icon: '🏗️',
  },
  {
    name: 'HTTP Request Builder',
    description: 'Interactive developer tool for constructing, sending, and analyzing HTTP requests directly from the browser',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/http-request-builder',
    icon: '🌐',
  },
  {
    name: 'CSV JSON Excel Converter',
    description: 'Convert between CSV, JSON, and Excel formats instantly — six conversion modes with browser-based processing',
    category: 'Developer',
    link: '/tools/developer/csv-json-converter',
    icon: '🔄',
  },
  {
    name: 'Excel to JSON Converter',
    description: 'Convert Excel spreadsheets to JSON with multi-sheet support and custom formatting options',
    category: 'Developer',
    link: '/tools/developer/excel-to-json-converter',
    icon: '📊',
  },
  {
    name: 'YAML to JSON Converter',
    description: 'Convert YAML to JSON or JSON to YAML with full support for comments and anchors',
    category: 'Developer',
    link: '/tools/developer/yaml-json-converter',
    icon: '🧾',
  },
  {
    name: 'Online Diff Checker & Code Compare',
    description: 'Compare text, code, and config files side by side with syntax highlighting. See additions, deletions, and changes instantly.',
    category: 'Developer',
    link: '/tools/developer/diff-checker',
    icon: '📝',
  },
  {
    name: 'GUID & UUID Generator',
    description: 'Generate GUIDs and UUIDs (v1, v4, v7) instantly. Bulk generation, format options, and copy to clipboard. Free online tool.',
    category: 'Developer',
    link: '/tools/developer/uuid-generator',
    icon: '🆔',
  },
  {
    name: 'Unix Epoch Time Converter',
    description: 'Convert Unix timestamps to dates and dates to epoch time instantly. Supports milliseconds, timezones, and multiple formats.',
    category: 'Developer',
    link: '/tools/developer/unix-timestamp-converter',
    icon: '⏱️',
    embed: 'hero',
  },
  {
    name: 'Windows FILETIME Converter',
    description: 'Convert Windows FILETIME, LDAP, and Active Directory timestamps to human-readable dates. Batch convert lastLogon, pwdLastSet, and accountExpires values.',
    category: 'Developer',
    link: '/tools/developer/filetime-converter',
    icon: '🪟',
  },
  {
    name: 'Lorem Ipsum & Placeholder Image Generator',
    description: 'Generate placeholder text and images for designs. Create Lorem Ipsum in 6 themes plus placeholder images from 7 providers with social media presets.',
    category: 'Developer',
    link: '/tools/developer/lorem-ipsum-generator',
    icon: '📄',
  },
  {
    name: 'ASCII Art Generator',
    description: 'Convert text and images to ASCII art. Text-to-ASCII with 200+ FIGlet fonts, image-to-ASCII with color output, edge detection, and dithering. Plus shapes and a pre-made art library.',
    category: 'Developer',
    link: '/tools/developer/ascii-art-generator',
    icon: '🎨',
  },
  {
    name: 'Advanced Word Counter',
    description: 'Real-time word counter with readability analysis, SEO optimization, keyword density, writing goals, and productivity tracking',
    category: 'Developer',
    link: '/tools/developer/word-counter',
    icon: '✍️',
  },
  {
    name: 'AI Writing Signal Analyzer',
    description: 'Highlight the stylistic markers that suggest text came from an AI model — invisible characters, em dash habits, assistant phrasing, overused vocabulary, and flat sentence rhythm. Shows what was flagged and what else causes it. Not a watermark detector and not proof of authorship.',
    category: 'Developer',
    link: '/tools/developer/ai-writing-signals',
    icon: '🔍',
    embed: 'hero',
  },
  {
    name: 'QR Code Generator',
    description: 'Create QR codes for URLs, text, and contact information',
    category: 'Developer',
    link: '/tools/developer/qr-code-generator',
    icon: '📱',
  },
  {
    name: 'QR Code Decoder',
    description: 'Decode any QR code from an image. Upload, drag, or paste a picture and read its contents — URLs, Wi-Fi credentials, contacts, and more — entirely in your browser. Links are shown, never auto-opened.',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/qr-code-decoder',
    icon: '📷',
  },
  {
    name: 'Color Picker & Converter - HEX, RGB, HSL',
    description: 'Pick colors and convert between HEX, RGB, HSL, and HSV formats. Includes palette generator, contrast checker, and CSS export.',
    category: 'Developer',
    link: '/tools/developer/color-picker',
    icon: '🎨',
  },
  {
    name: 'HTML Entity Encoder & Decoder',
    description: 'Encode and decode HTML entities online with XSS prevention analysis. Supports named and numeric entities.',
    category: 'Developer',
    link: '/tools/developer/html-encoder',
    icon: '🏷️',
    embed: 'hero',
  },
  {
    name: 'HTTP Status Codes',
    description: 'Comprehensive HTTP status code reference with search and explanations',
    category: 'Developer',
    link: '/tools/developer/http-status-codes',
    icon: '📡',
  },
  {
    name: 'Git Command Reference',
    description: 'Searchable Git workflow cookbook with safe undo commands and best practices',
    category: 'Developer',
    link: '/tools/developer/git-command-reference',
    icon: '</>',
  },
  {
    name: 'Git Branch Visualizer',
    description: 'Compare Git branching strategies visually. Understand GitFlow, GitHub Flow, and trunk-based development with diagrams and commands.',
    category: 'Developer',
    link: '/tools/developer/git-branch-visualizer',
    icon: '🌿',
  },
  {
    name: 'Data Format Converter',
    description: 'Convert between JSON, YAML, XML, TOML, and CSV with real-time validation',
    category: 'Developer',
    link: '/tools/developer/data-format-converter',
    icon: '🔄',
  },
  {
    name: 'JSON Formatter',
    description: 'Format, validate, and minify JSON with syntax highlighting',
    category: 'Developer',
    link: '/tools/developer/json-formatter',
    icon: '{ }',
  },
  {
    name: 'Number Base Converter',
    description: 'Convert between binary, octal, decimal, and hexadecimal',
    category: 'Developer',
    link: '/tools/developer/number-base-converter',
    icon: '0b',
  },
  {
    name: 'JavaScript Animation Playground',
    description: 'Edit and run canvas, WebGL, three.js and CSS animations live',
    category: 'Developer',
    link: '/tools/developer/javascript-animation-playground',
    icon: '✨',
  },
  {
    name: 'Vanity Phone Number Generator',
    description: 'Turn a word into phone keypad digits (FLOWERS → 356-9377), or find the English words a phone number spells',
    category: 'Planning',
    link: '/tools/planning/vanity-phone-number-generator',
    icon: '☎️',
  },
  {
    name: 'RAID Reliability Simulator',
    description: 'Capacity plus the part that matters: rebuild window, URE odds and the probability an array loses data',
    category: 'Planning',
    link: '/tools/planning/raid-reliability-calculator',
    icon: '💽',
  },
  {
    name: 'Mandelbrot Explorer',
    description: 'Zoom and pan the Mandelbrot set in real time, with Julia sets and PNG export',
    category: 'Developer',
    link: '/tools/developer/mandelbrot-explorer',
    icon: '🌀',
  },
  {
    name: 'Spirograph Generator',
    description: 'Draw spirograph and harmonograph figures, then export a PNG',
    category: 'Developer',
    link: '/tools/developer/spirograph-generator',
    icon: '🎡',
  },
  {
    name: 'Falling Sand Game',
    description: 'A multi-material sandbox: sand, water, oil, fire, lava, acid, plants and steam, all interacting',
    category: 'Developer',
    link: '/tools/developer/falling-sand-game',
    icon: '⏳',
  },
  {
    name: 'Sorting Algorithm Visualizer',
    description: 'Watch bubble, insertion, selection, merge and quicksort run step by step with live comparison counts',
    category: 'Developer',
    link: '/tools/developer/sorting-algorithm-visualizer',
    icon: '📊',
  },
  {
    name: 'Game of Life Simulator',
    description: "Conway's Game of Life with glider guns, pulsars and a grid you can draw on",
    category: 'Developer',
    link: '/tools/developer/game-of-life-simulator',
    icon: '🦠',
  },
  {
    name: 'Double Pendulum Simulator',
    description: 'The real equations of motion, with a ghost copy that shows how fast chaos diverges',
    category: 'Developer',
    link: '/tools/developer/double-pendulum-simulator',
    icon: '🌀',
  },
  {
    name: 'Voronoi Diagram Generator',
    description: 'Generate drifting Voronoi cell patterns on the GPU and export a PNG',
    category: 'Developer',
    link: '/tools/developer/voronoi-diagram-generator',
    icon: '🔷',
  },
  {
    name: 'Matrix Rain Generator',
    description: 'Generate the Matrix digital rain effect and export video or HTML',
    category: 'Developer',
    link: '/tools/developer/matrix-rain-generator',
    icon: '🟩',
  },
  {
    name: 'Webhook Tester & Inspector',
    description: 'Generate temporary webhook endpoints, capture payloads, and validate signatures for GitHub, Stripe, Shopify, and Slack.',
    category: 'Developer',
    link: '/tools/developer/webhook-tester-inspector',
    embed: 'card',
    icon: '🪝',
  },
  {
    name: 'JSON Validator & Formatter',
    description: 'Validate and format JSON with detailed error messages, tree visualization, and syntax highlighting. Fix JSON errors instantly.',
    category: 'Developer',
    link: '/tools/developer/json-validator',
    icon: '✓',
  },
  {
    name: 'Online Regex Tester & Debugger',
    description: 'Test regular expressions online with live matching, pattern explanations, and a built-in regex library. Supports JavaScript, Python, and Go.',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/regex-tester',
    icon: '.*',
  },
  {
    name: 'Grok Pattern Builder & Debugger',
    description: 'Build, test, and debug Grok patterns online with live matching, fix suggestions, and export to regex, Logstash config, and Elasticsearch ingest pipelines.',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/grok-pattern-builder',
    embed: 'hero',
    icon: '%{ }',
  },
  {
    name: 'Neural Network Playground',
    description: 'Train a real neural network in your browser — no code, no installs. Pick a sample dataset (Titanic survival, vehicle classifier, handwritten digits) or upload your own CSV, watch the network learn with live visuals, then ask it questions and export the model as Python/Keras code.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/neural-network-playground',
    embed: 'hero',
    icon: '🧠',
    howToSteps: [
      { name: 'Choose a dataset', text: 'Pick Titanic survival, the vehicle classifier, handwritten digits, or upload your own CSV file. The data is processed entirely in your browser.' },
      { name: 'Design your network', text: 'Add hidden layers, set neurons per layer, and choose the activation function, optimizer, learning rate, and number of training epochs.' },
      { name: 'Train and watch it learn', text: 'Start training and watch the loss fall, accuracy climb, and connection weights strengthen in real time. Training runs on your device and takes under a minute.' },
      { name: 'Ask the model questions', text: 'Use sliders and dropdowns (or draw a digit) to query your trained model and see live predictions with confidence scores. Export the equivalent Python/Keras code.' },
    ],
  },
  {
    name: 'URL Encoder',
    description: 'Encode and decode URLs with component breakdown and query parsing',
    category: 'Developer',
    link: '/tools/developer/url-encoder',
    icon: '🔗',
  },
  {
    name: 'User Agent Parser, Analyzer & Builder',
    description: 'Parse user agent strings to detect browser, OS, device type, and bots, see every signal your own browser leaks, and build custom user agent strings for testing.',
    category: 'Developer',
    link: '/tools/developer/user-agent-parser',
    icon: '🌐',
  },
  {
    name: 'Markdown Preview & Editor',
    description: 'Live markdown editor with instant preview. Supports GitHub Flavored Markdown, tables, code blocks, and task lists.',
    category: 'Developer',
    link: '/tools/developer/markdown-preview',
    icon: '📝',
  },
  {
    name: 'Word Cloud Generator',
    description: 'Generate customizable word clouds from raw text with filters and export options',
    category: 'Media',
    link: '/tools/media/wordcloud-generator',
    icon: '☁️',
  },
  {
    name: 'Rate Limit Calculator',
    description: 'Model API throttling budgets, per-client limits, and queue sizing to avoid 429 errors',
    category: 'Developer',
    link: '/tools/developer/rate-limit-calculator',
    icon: '🚦',
  },
  {
    name: 'SQL Formatter',
    description: 'Format and beautify SQL queries with multi-dialect support',
    category: 'Developer',
    link: '/tools/developer/sql-formatter',
    icon: '💾',
  },
  {
    name: 'SQL Dialect Translator',
    description: 'Translate SQL between dialects - auto-detects source dialect and converts syntax for MySQL, PostgreSQL, SQL Server, Oracle, SQLite, BigQuery, Redshift, and Snowflake',
    category: 'Developer',
    link: '/tools/developer/sql-converter',
    icon: '🔄',
  },
  {
    name: 'Cron Expression Builder',
    description: 'Build and validate cron expressions with visual interface',
    category: 'Developer',
    link: '/tools/developer/cron-builder',
    icon: '⏰',
    embed: 'hero',
  },
  {
    name: 'Timezone Meeting Planner & World Clock',
    description: 'Free meeting planner for distributed teams. Find overlap windows across 38 time zones, schedule calls, convert times, and export to calendar.',
    category: 'Developer',
    link: '/tools/developer/world-clock',
    icon: '🌍',
  },
  {
    name: 'SRI Hash Generator',
    description: 'Generate Subresource Integrity hashes for scripts and stylesheets',
    category: 'Developer',
    secondary_categories: ['Security'],
    link: '/tools/developer/sri-hash-generator',
    icon: '🔐',
  },
  {
    name: 'TLD Enumerator',
    description: 'Generate domain variations across all TLDs for brand protection and research',
    category: 'Developer',
    link: '/tools/developer/tld-enumerator',
    icon: '🌐',
  },
  {
    name: 'Semver Calculator',
    description: 'Parse, validate, compare, and increment semantic versions with npm-style range checking',
    category: 'Developer',
    link: '/tools/developer/semver-calculator',
    icon: '🔢',
  },
  {
    name: 'Docker Command Builder',
    description: 'Visual Docker command generator for run, build, and compose with security best practices and preset templates',
    category: 'Developer',
    link: '/tools/developer/docker-command-builder',
    icon: '🐳',
  },
  {
    name: 'Mock Data Generator',
    description: 'Generate realistic fake test data (names, emails, addresses, credit cards, UUIDs) with JSON, CSV, and SQL export',
    category: 'Developer',
    link: '/tools/developer/mock-data-generator',
    icon: '🎲',
  },
  {
    name: '.gitignore Generator',
    description: 'Generate customized .gitignore files by combining templates for languages, frameworks, IDEs, and operating systems',
    category: 'Developer',
    link: '/tools/developer/gitignore-generator',
    icon: '📄',
  },
  // Planning Tools
  {
    name: 'Cybersecurity Budget Calculator',
    description: 'Calculate recommended cybersecurity budget based on industry benchmarks, compliance needs, and risk factors',
    category: 'Planning',
    link: '/tools/planning/cybersecurity-budget-calculator',
    icon: '💰',
  },
  {
    name: 'Data Breach Cost Calculator',
    description: 'Estimate the potential financial impact of a data breach using IBM Cost of a Data Breach 2026 benchmarks',
    category: 'Planning',
    link: '/tools/planning/data-breach-cost-calculator',
    icon: '💸',
  },
  {
    name: 'Cybersecurity ROI Calculator',
    description: 'Justify security investments with ROI calculations, payback periods, and NPV analysis',
    category: 'Planning',
    link: '/tools/planning/cybersecurity-roi-calculator',
    icon: '📊',
  },
  {
    name: 'Cybersecurity Maturity Assessment',
    description: 'Evaluate your security posture across 9 domains and get a personalized improvement roadmap',
    category: 'Assessment',
    link: '/tools/assessment/cybersecurity-maturity-assessment',
    embed: 'card',
    icon: '📋',
  },
  {
    name: 'EDR Needs Assessment',
    description: 'Find out if your small business needs basic antivirus, EDR, or managed MDR with personalized recommendations',
    category: 'Assessment',
    link: '/tools/assessment/edr-needs-assessment',
    icon: '🛡️',
  },
  {
    name: 'CI/CD Security Checklist',
    description: 'Assess CI/CD pipeline security across secrets management, SAST/DAST, artifact signing, SLSA framework, and deployment controls.',
    category: 'Assessment',
    link: '/tools/assessment/cicd-security-checklist',
    icon: '🔒',
  },
  {
    name: 'Business Case Builder',
    description: 'Build a compelling cybersecurity business case with a guided wizard combining risk assessment, breach cost, budget, and ROI analysis',
    category: 'Planning',
    link: '/tools/business-case-builder',
    icon: '📑',
  },
  {
    name: 'Vendor Risk Management "Breach-Proof" Scorecard',
    description: 'Convert vendor risk (VRM) maturity into Annual Loss Expectancy and receive a tailored vendor action plan',
    category: 'Compliance',
    link: '/tools/compliance/vrm-breach-proof-scorecard',
    icon: '🛡️',
  },
  {
    name: 'Cloud Security Self-Assessment (iCSAT)',
    description: 'Interactive cloud security benchmark for AWS, Azure, and GCP with instant remediation guidance and compliance insights',
    category: 'Assessment',
    link: '/tools/assessment/cloud-security-self-assessment',
    embed: 'card',
    icon: '☁️',
  },
  {
    name: 'Cloud Cost Comparison',
    description: 'Compare cloud computing costs across AWS, Azure, Google Cloud, and Oracle Cloud. Get instant pricing estimates for compute instances from published list prices',
    category: 'Planning',
    link: '/tools/planning/cloud-cost-comparison',
    icon: '💰',
  },
  {
    name: 'Cloud Carbon Footprint Estimator',
    description: 'Estimate cloud emissions by provider and region, then model rightsizing and greener deployment scenarios to reduce carbon impact.',
    category: 'Planning',
    link: '/tools/planning/cloud-carbon-footprint-estimator',
    icon: '🌍',
  },
  {
    name: 'Route 53 Pricing Calculator',
    description: 'Calculate AWS Route 53 DNS hosting costs including hosted zones, queries, health checks, and Traffic Flow policies',
    category: 'Planning',
    link: '/tools/planning/route53-pricing-calculator',
    icon: '🌐',
  },
  {
    name: 'AWS S3 Command Generator',
    description: 'Generate aws s3 sync, cp, and mv commands with the right flags. Build complex S3 CLI commands without memorizing syntax',
    category: 'Developer',
    link: '/tools/developer/aws-s3-command-generator',
    icon: '📦',
  },
  {
    name: 'journalctl Command Builder',
    description: 'Interactive systemd journal query builder: filter logs by service, priority, time range, and grep pattern, then copy the journalctl command',
    category: 'Developer',
    link: '/tools/journalctl-builder',
    icon: '📜',
  },
  {
    name: 'LLM Token Counter',
    description: 'Count tokens in text for GPT, Claude, Gemini, and open models like Llama, Gemma, and Qwen — or search any model on Hugging Face. Estimate API costs, check context window fit, and optimize prompts',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/llm-token-counter',
    icon: '🔢',
    embed: 'hero',
  },
  {
    name: 'Codex Usage Tracker',
    description: 'See what your ChatGPT plan\'s Codex allowance is worth in API dollars, your weekly burn rate, and the cheapest plan for your usage. Reads your local session files in the browser',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/codex-usage-tracker',
    icon: '📊',
  },
  {
    name: 'TTS & Speech-to-Text API Cost Calculator',
    description: 'Compare monthly text-to-speech and speech-to-text API costs across ElevenLabs, OpenAI, Google Cloud, Amazon Polly and Transcribe, Azure, Deepgram and AssemblyAI. Sourced, dated prices',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/tts-stt-api-cost-calculator',
    icon: '🗣️',
  },
  {
    name: 'AWS Bedrock Pricing Calculator',
    description: 'Calculate AWS Bedrock costs for Claude, Llama, Titan, and other AI models. Estimate input/output token costs for your workload',
    category: 'Planning',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/planning/aws-bedrock-pricing-calculator',
    icon: '🤖',
    embed: 'hero',
  },
  {
    name: 'Microsoft Copilot Cost Calculator',
    description: 'Estimate Microsoft Copilot Cowork spend by persona and task mix. Model Copilot Credits, compare pay-as-you-go vs pre-purchase (P3), and see the savings from routing work to cheaper model tiers',
    category: 'Planning',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/planning/microsoft-copilot-cost-calculator',
    icon: '🧮',
  },
  {
    name: 'Ransomware Resilience Assessment (IRRA)',
    description: 'Interactive ransomware recovery diagnostic that measures immutability, RTO delta, and SaaS gaps with instant remediation roadmap',
    category: 'Assessment',
    link: '/tools/assessment/interactive-ransomware-resilience-assessment',
    embed: 'card',
    icon: '🧯',
  },
  {
    name: 'Risk Matrix Calculator',
    description: 'Score likelihood and impact, generate heat maps, and build risk registers aligned to NIST, ISO 27005, and FAIR frameworks',
    category: 'Assessment',
    secondary_categories: ['Planning'],
    link: '/tools/assessment/risk-matrix-calculator',
    icon: '📊',
  },
  {
    name: 'Backup Recovery Time Calculator',
    description: 'Calculate optimal RTO/RPO targets, analyze downtime costs, and compare backup strategies with cost-benefit analysis',
    category: 'Planning',
    link: '/tools/planning/backup-recovery-time-calculator',
    icon: '💾',
  },
  {
    name: 'VM Resource Calculator',
    description: 'Calculate virtual machine resource requirements for any workload. Get vCPU, RAM, storage specs with cloud cost estimates.',
    category: 'Planning',
    link: '/tools/planning/vm-resource-calculator',
    icon: '🖥️',
  },
  {
    name: 'Business Impact Calculator',
    description: 'Conduct a Business Impact Analysis (BIA) to identify critical functions, assess impact by category, calculate recovery priorities, and determine Maximum Tolerable Downtime (MTD)',
    category: 'Assessment',
    secondary_categories: ['Planning'],
    link: '/tools/assessment/business-impact-calculator',
    icon: '📈',
  },
  {
    name: 'Dependency Atlas',
    description: 'Map business and technology dependencies, simulate outage or breach blast radius, and document tabletop findings in a private browser workspace.',
    category: 'Planning',
    secondary_categories: ['Security', 'Assessment'],
    link: '/tools/planning/dependency-atlas',
    icon: '🕸️',
  },
  {
    name: 'MTBF/MTTR Reliability Calculator',
    description: 'Calculate system reliability metrics (MTBF, MTTR, MTTA, MTTD, MTTF), analyze availability, estimate downtime costs, track SLA compliance, and improve operational reliability with data-driven insights',
    category: 'Planning',
    link: '/tools/planning/mtbf-mttr-calculator',
    icon: '📊',
  },
  {
    name: 'SLA/SLO Calculator',
    description: 'Calculate service level objectives, error budgets, burn rates, and downtime costs. Compare multi-tier SLAs based on Google SRE best practices',
    category: 'Planning',
    link: '/tools/planning/sla-slo-calculator',
    icon: '🎯',
  },
  {
    name: 'Time Duration Calculator',
    description: 'Calculate time between two dates, add/subtract hours and minutes, and convert time units. Free calculator for project planning.',
    category: 'Planning',
    link: '/tools/planning/time-duration-calculator',
    icon: '⏱️',
  },
  {
    name: 'Timeline Generator',
    description: 'Create visual timelines for projects, events, and milestones. Export as image or PDF for presentations and documentation.',
    category: 'Planning',
    link: '/tools/planning/timeline-generator',
    icon: '📅',
  },
  {
    name: 'File Metadata Analyzer',
    description: 'Extract EXIF/GPS from photos, PDF properties, audio ID3 tags, and Office document metadata. View GPS on Google Maps. Export CSV reports.',
    category: 'Developer',
    link: '/tools/developer/file-metadata-analyzer',
    icon: '📄',
  },
  {
    name: 'Book Page Size Converter',
    description: 'Convert page counts between book trim sizes (8.5×11 to 6×9), calculate words per page, estimate spine width, and verify KDP compatibility',
    category: 'Developer',
    link: '/tools/developer/book-page-size-converter',
    icon: '📖',
  },
  {
    name: 'Webhook Payload Generator',
    description: 'Generate webhook payloads with HMAC signatures for Stripe, GitHub, Slack, Shopify, Twilio, SendGrid, Discord and more',
    category: 'Developer',
    link: '/tools/developer/webhook-payload-generator',
    icon: '🔗',
  },
  // Media & File Tools
  {
    name: 'WebP to GIF Converter',
    description: 'Convert WebP images and animated WebP to GIF format instantly. High quality, fast, and works entirely in your browser.',
    category: 'Developer',
    secondary_categories: ['Media'],
    link: '/tools/developer/webp-to-gif-converter',
    icon: '🖼️',
  },
  {
    name: 'Image to PDF Converter',
    description: 'Convert photos to PDF on iPhone, Android, or desktop. Mobile-optimized tool for combining images into PDF documents.',
    category: 'Developer',
    link: '/tools/developer/image-to-pdf-converter',
    icon: '📄',
  },
  {
    name: 'HEIC to JPG Converter',
    description: 'Convert iPhone HEIC photos to JPG format for Windows compatibility. Batch conversion with privacy-first design.',
    category: 'Developer',
    secondary_categories: ['Media'],
    link: '/tools/developer/heic-to-jpg-converter',
    icon: '📷',
  },
  {
    name: 'Image Format Converter',
    description: 'Universal image converter supporting 10+ formats: JPEG, PNG, WebP, GIF, HEIC, AVIF, TIFF, BMP, ICO, SVG, and PDF output.',
    category: 'Developer',
    secondary_categories: ['Media'],
    link: '/tools/developer/image-format-converter',
    icon: '🔄',
  },
  {
    name: 'Image Compressor',
    description: 'Compress images to a target file size while preserving quality. Reduce JPEG, PNG, WebP sizes for email or web.',
    category: 'Media',
    link: '/tools/media/image-compressor',
    icon: '📦',
  },
  {
    name: 'GDPR Role & Retention Mapper',
    description: 'Determine your GDPR role (Controller/Processor/Joint Controller), map data processing activities, calculate retention periods, select legal bases, and assess cross-border transfer requirements',
    category: 'Compliance',
    link: '/tools/compliance/gdpr-role-retention-mapper',
    embed: 'card',
    icon: '🇪🇺',
  },
  {
    name: 'HIPAA Quick Assessment',
    description: 'Free 3-minute HIPAA compliance check for healthcare practices',
    category: 'Compliance',
    link: '/tools/compliance/hipaa-quick-assessment',
    embed: 'card',
    icon: '🏥',
  },
  {
    name: 'SOC 2 Type II Gap Analysis',
    description: 'Assess SOC 2 readiness across all 5 Trust Service Criteria with maturity scoring, gap analysis, and prioritized remediation roadmap',
    category: 'Compliance',
    link: '/tools/compliance/soc2-gap-analysis',
    icon: '🛡️',
  },
  {
    name: 'Compliance Readiness Checklist',
    description: 'Interactive checklists for HIPAA, SOC 2, PCI-DSS, and more',
    category: 'Compliance',
    link: '/tools/compliance/compliance-checklist',
    icon: '✅',
    comingSoon: true,
  },
  // Cipher Tools
  {
    name: 'Caesar Cipher Decoder & Encoder',
    description: 'Decode and encode Caesar cipher text online. Brute force all 25 shifts, frequency analysis, auto-detection, and interactive cipher wheel.',
    category: 'Security',
    link: '/tools/security/caesar-cipher',
    icon: '🔠',
  },
  {
    name: 'Vigenère Cipher',
    description: 'Polyalphabetic cipher with Kasiski examination, Index of Coincidence analysis, key recovery, and Tabula Recta visualization',
    category: 'Security',
    link: '/tools/security/vigenere-cipher',
    icon: '🔐',
  },
  {
    name: 'Substitution Cipher',
    description: 'Monoalphabetic substitution cipher with interactive solving assistant, frequency analysis, and pattern finder',
    category: 'Security',
    link: '/tools/security/substitution-cipher',
    icon: '🔤',
  },
  {
    name: 'Cipher Suite',
    description: 'Collection of classical cipher tools for encryption, decryption, and cryptanalysis including Caesar, Vigenère, and substitution ciphers',
    category: 'Security',
    link: '/tools/security/cipher-suite',
    icon: '🗝️',
  },
  {
    name: 'Cipher Identifier',
    description: 'Decode and identify unknown ciphers: Caesar, ROT13, Vigenère, Atbash, Base64, Morse and more, ranked by frequency analysis and Index of Coincidence',
    category: 'Security',
    link: '/tools/security/cipher-identifier',
    icon: '🔍',
  },
  {
    name: 'Affine Cipher',
    description: 'Encrypt and decrypt with affine cipher (ax + b mod 26). Includes brute force mode and modular inverse calculator',
    category: 'Security',
    link: '/tools/security/affine-cipher',
    icon: '🔢',
  },
  {
    name: 'Atbash Cipher',
    description: 'Ancient Hebrew reverse alphabet cipher (A↔Z, B↔Y). Self-inverse cipher with visual alphabet mapping',
    category: 'Security',
    link: '/tools/security/atbash-cipher',
    icon: '🔃',
  },
  {
    name: 'Rail Fence Cipher',
    description: 'Transposition cipher that writes text in zigzag pattern. Visual diagram and brute force mode included',
    category: 'Security',
    link: '/tools/security/rail-fence-cipher',
    icon: '📊',
  },
  {
    name: 'Playfair Cipher',
    description: 'Digraph substitution cipher using 5×5 keyword matrix. Includes visual matrix and encoding rules',
    category: 'Security',
    link: '/tools/security/playfair-cipher',
    icon: '🔲',
  },
  {
    name: 'Beaufort Cipher',
    description: 'Self-reciprocal polyalphabetic cipher similar to Vigenère. Same operation encrypts and decrypts with C = (K - P) mod 26',
    category: 'Security',
    link: '/tools/security/beaufort-cipher',
    icon: '🔁',
  },
  {
    name: 'Hill Cipher',
    description: 'Matrix-based polygraphic cipher using linear algebra. Supports 2×2 and 3×3 key matrices with inverse calculator',
    category: 'Security',
    link: '/tools/security/hill-cipher',
    icon: '📐',
  },
  {
    name: 'Enigma Machine Simulator',
    description: 'Interactive WWII Enigma cipher machine simulator with authentic rotors (I-V), reflectors, plugboard, and signal visualization',
    category: 'Security',
    link: '/tools/security/enigma-simulator',
    icon: '⚙️',
  },
  {
    name: 'One-Time Pad',
    description: 'Theoretically unbreakable cipher with random key generation. XOR encryption with perfect secrecy when used correctly',
    category: 'Security',
    link: '/tools/security/one-time-pad',
    icon: '🛡️',
  },
  {
    name: 'RSA Playground',
    description: 'Interactive RSA encryption with small primes. See key generation, encryption, decryption step-by-step. Break RSA to understand why key size matters.',
    category: 'Security',
    link: '/tools/security/rsa-playground',
    icon: '🔐',
  },
  {
    name: 'Public Key Concepts',
    description: 'Understand public/private key cryptography through visual analogies - padlock, color mixing, and mailbox demos. No math required.',
    category: 'Security',
    link: '/tools/security/public-key-concepts',
    icon: '🔑',
  },
  {
    name: 'Encoding Chain Analyzer',
    description: 'Detect and decode nested encodings (Base64→Hex→URL→ROT13). Visualize decoding chains for CTF and forensics',
    category: 'Security',
    link: '/tools/security/encoding-chain-analyzer',
    icon: '🔗',
  },
  {
    name: 'OCSP/CRL Revocation Checker',
    description: 'Extract OCSP responder URLs and CRL distribution points from X.509 certificates. Check certificate revocation status',
    category: 'Security',
    link: '/tools/security/ocsp-crl-checker',
    icon: '📋',
  },
  {
    name: 'Biometric Performance Simulator',
    description: 'Interactive FAR/FRR curves with sensitivity slider, CER calculation, and modality comparison for CISSP Domain 5 (IAM)',
    category: 'Security',
    link: '/tools/security/biometric-performance-simulator',
    embed: 'card',
    icon: '🔬',
  },
  // PowerShell Generators
  {
    name: 'RAID Simulator',
    description: 'Build virtual RAID 0–6, 0+1, 10, 50, and 60 arrays. Route text and sized payloads, model controller caching, inject failures, and watch rebuilds and RAID reshapes.',
    category: 'Developer',
    link: '/tools/developer/raid-simulator',
    icon: '💾',
    embed: 'hero',
  },
  {
    name: 'Exchange Online PowerShell Command Builder',
    description: 'Build complete Exchange Online PowerShell scripts for mailbox permissions, forwarding, shared mailboxes, distribution groups, message traces, calendar permissions, transport rules, and reporting. Every script includes the Connect-ExchangeOnline preamble.',
    category: 'Developer',
    link: '/tools/developer/exchange-online-powershell',
    icon: '📧',
    embed: 'hero',
  },
  {
    name: 'Windows Update PowerShell Generator',
    description: 'Generate Windows Update commands for local and remote computers using PSWindowsUpdate, usoclient, and wuauclt. Pick an action, choose local or remote targets, and chain steps into one copy-paste script.',
    category: 'Developer',
    link: '/tools/developer/windows-update-powershell',
    icon: '🔄',
    embed: 'hero',
  },
  {
    name: 'Virsh Command Builder',
    description: 'Build safe, copy-paste virsh commands for local or remote KVM/libvirt virtual machines. Inspect domains, manage lifecycle, change CPU and memory, work with consoles and saved state, and identify destructive operations before running them.',
    category: 'Developer',
    link: '/tools/developer/virsh-command-builder',
    icon: '🖥️',
    embed: 'hero',
  },
  {
    name: 'Group Policy Command Builder',
    description: 'Build gpupdate, gpresult, and Invoke-GPUpdate commands for forcing policy refreshes, generating RSoP reports, and backing up GPOs — local or remote, user or computer scope, in one copy-paste script.',
    category: 'Developer',
    link: '/tools/developer/group-policy-commands',
    icon: '🛡️',
    embed: 'hero',
  },
  {
    name: 'Traceroute Command Builder',
    description: 'Build tracert, pathping, traceroute, and mtr commands to trace the network path to any host — choose the tool, target, hop limit, timeout, and protocol.',
    category: 'Network',
    link: '/tools/network/tracert-command-builder',
    icon: '🛰️',
    embed: 'hero',
  },
  {
    name: 'netstat Command Builder',
    description: 'Build netstat and PowerShell Get-NetTCPConnection commands to find which process owns a port, list listening ports, view the routing table, and check protocol stats.',
    category: 'Network',
    link: '/tools/network/netstat-command-builder',
    icon: '🔌',
    embed: 'hero',
  },
  {
    name: 'winget Command Builder',
    description: 'Build Windows Package Manager (winget) commands to search, install, upgrade, uninstall, and export apps — with exact-match, silent, version, and scope flags.',
    category: 'Developer',
    link: '/tools/developer/winget-command-builder',
    icon: '📦',
    embed: 'hero',
  },
  {
    name: 'Rsync Command Builder',
    description: 'Build rsync commands for backups, mirrors and server migrations — archive mode, SSH transport, deletion modes, filters, bandwidth limits, and a dry run that catches the trailing-slash trap.',
    category: 'Developer',
    link: '/tools/developer/rsync-command-builder',
    icon: '🔃',
    embed: 'hero',
  },
  {
    name: 'Robocopy Command Builder',
    description: 'Build robocopy commands to copy, mirror, or move files with the right switches — multithreading, retries, exclusions, logging, and a safe list-only test mode.',
    category: 'Developer',
    link: '/tools/developer/robocopy-command-builder',
    icon: '🗂️',
    embed: 'hero',
  },
  {
    name: 'Network Reset Command Builder',
    description: 'Build the Windows network reset commands — netsh winsock reset, netsh int ip reset, ipconfig flushdns, release and renew — with the correct sequence and reboot guidance.',
    category: 'Network',
    link: '/tools/network/reset-network-command-builder',
    icon: '🔄',
    embed: 'hero',
  },
  {
    name: 'diskpart Command Builder',
    description: 'Build diskpart scripts to list, clean, partition, format, and convert disks — with built-in verify steps and warnings so you do not wipe the wrong drive.',
    category: 'Developer',
    link: '/tools/developer/diskpart-command-builder',
    icon: '💽',
    embed: 'hero',
  },
  {
    name: 'PsExec Command Builder',
    description: 'Build PsExec commands to run programs on remote Windows machines — run as SYSTEM, interactive, copy-to-remote, and credential options, with password prompting kept off the command line.',
    category: 'Developer',
    link: '/tools/developer/psexec-command-builder',
    icon: '🖥️',
    embed: 'hero',
  },
  {
    name: 'Windows Repair Command Builder',
    description: 'Build SFC, DISM, and chkdsk repair commands in the correct order — RestoreHealth source options, chkdsk fix and recover flags, and a one-click full repair sequence.',
    category: 'Developer',
    link: '/tools/developer/repair-windows-command-builder',
    icon: '🛠️',
    embed: 'hero',
  },
  {
    name: 'Azure AD PowerShell Command Builder',
    description: 'Build Microsoft Graph PowerShell scripts for Entra ID user management, groups, MFA status, licensing, roles, and sign-in reports — each with the minimal Connect-MgGraph scopes required.',
    category: 'Developer',
    link: '/tools/developer/azure-ad-powershell',
    icon: '☁️',
    embed: 'hero',
  },
  // Command Builders
  {
    name: 'CrowdStrike Falcon Command Builder',
    description: 'Generate CrowdStrike Falcon sensor commands for Windows, macOS, and Linux: install with CID and provisioning tokens, uninstall with a maintenance token, verify the sensor is running, configure proxies, and set grouping tags.',
    category: 'Security',
    link: '/tools/security/crowdstrike-falcon-commands',
    icon: '🛡️',
    embed: 'hero',
  },
  {
    name: 'curl Command Builder',
    description: 'Build curl commands with headers, auth, JSON bodies, and shell-aware quoting for bash, PowerShell, and cmd.exe — plus curl install commands for Windows, macOS, and Linux.',
    category: 'Developer',
    link: '/tools/developer/curl-command-builder',
    icon: '🌐',
    embed: 'hero',
  },
  {
    name: 'LLM VRAM Calculator',
    description: 'Calculate how much VRAM any LLM needs to run locally — pick a model (Llama, Gemma, Qwen, DeepSeek, or search Hugging Face), choose a quantization and context size, and see which GPUs it fits on, including multi-GPU setups.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/llm-vram-calculator',
    icon: '🧠',
    embed: 'card',
    howToSteps: [
      { name: 'Pick a model', text: 'Choose a popular open model, search Hugging Face for any model, or enter custom architecture specs.' },
      { name: 'Choose quantization and context', text: 'Select a quantization level (FP16 down to 2-bit) and slide the context window to the size you plan to use.' },
      { name: 'Check GPU fit', text: 'See total VRAM needed, download size, and which GPUs (or how many) can run it — including Apple Silicon unified memory.' },
    ],
  },
  {
    name: 'LLM Tokens Per Second Calculator',
    description: 'Estimate LLM tokens per second from memory bandwidth, model size, quantization, and context window. Compare generation speed across GPUs (RTX 4090, 5090, A100, H100, Apple Silicon) and understand the memory-bandwidth bottleneck.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/llm-inference-speed-calculator',
    icon: '⚡',
    embed: 'card',
    howToSteps: [
      { name: 'Pick a model and quantization', text: 'Choose any open model and the quantization you plan to run it at.' },
      { name: 'Select your hardware', text: 'Pick a GPU from the list or enter custom memory bandwidth and compute specs, including multi-GPU configurations.' },
      { name: 'Read the speed estimate', text: 'See estimated tokens/second, time-to-first-token, and how your hardware compares to other GPUs.' },
    ],
  },
  {
    name: 'What LLM Can I Run?',
    description: 'Detect your GPU with one click and see which LLMs your computer can actually run — Llama, Gemma, Qwen, DeepSeek and 50+ more, ranked by whether they fit in your VRAM, need CPU offloading, or won\'t run at all.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/what-llm-can-i-run',
    icon: '🎯',
    embed: 'card',
    howToSteps: [
      { name: 'Detect your hardware', text: 'Click "Detect my hardware" — your browser identifies your GPU — then confirm your memory amounts.' },
      { name: 'Review your tiers', text: 'Models are ranked: runs great, runs with trade-offs, technically runs, or won\'t fit.' },
      { name: 'Pick a model', text: 'See the best chat, coding, and reasoning models for your machine, with links to detailed VRAM and speed breakdowns.' },
    ],
  },
  {
    name: 'Fine-Tuning VRAM Calculator',
    description: 'Calculate VRAM requirements for fine-tuning LLMs with full fine-tuning, LoRA, or QLoRA. Accounts for gradients, optimizer states, activations, batch size, and gradient checkpointing — see which GPUs can train each model.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/fine-tuning-vram-calculator',
    icon: '🎛️',
    embed: 'card',
    howToSteps: [
      { name: 'Pick a model and method', text: 'Choose a base model and whether you\'re doing full fine-tuning, LoRA, or QLoRA.' },
      { name: 'Set training parameters', text: 'LoRA rank, batch size, sequence length, gradient checkpointing, and optimizer.' },
      { name: 'Compare and check fit', text: 'See the memory breakdown, compare all three methods side by side, and check which GPUs can handle the job.' },
    ],
  },
  {
    name: 'Ollama Command Builder',
    description: 'Build Ollama commands without memorizing syntax: run, pull, and create commands for any model, complete Modelfiles with parameters and system prompts, and server environment configuration — with built-in VRAM checks.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/ollama-command-builder',
    icon: '🦙',
    embed: 'card',
    howToSteps: [
      { name: 'Pick a model', text: 'Choose from popular models or search Hugging Face — the tool maps it to the right Ollama tag or hf.co pull syntax.' },
      { name: 'Choose an action and options', text: 'Run, pull, create from Modelfile, or configure the server — set context size, temperature, and system prompts.' },
      { name: 'Copy your commands', text: 'Chain multiple steps into a single copy-paste script, with Windows and macOS/Linux variants.' },
    ],
  },
  {
    name: 'Self-Hosted LLM Cost Calculator',
    description: 'Is it cheaper to self-host an LLM or use an API? Compare GPT, Claude, and Gemini API costs against running open models on your own hardware or cloud GPUs — with break-even timelines and capacity checks.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning', 'Planning'],
    link: '/tools/developer/self-hosted-llm-cost-calculator',
    icon: '💰',
    embed: 'card',
    howToSteps: [
      { name: 'Set your usage', text: 'Pick a usage preset or enter your monthly input/output token volumes.' },
      { name: 'Choose what to compare', text: 'Select an API model and a self-hosting option (buy hardware, rent cloud GPUs, or use what you own).' },
      { name: 'Read the verdict', text: 'See monthly costs, the break-even timeline, and whether your hardware can actually serve your volume.' },
    ],
  },
  {
    name: 'GPU Rental Cost Calculator',
    description: 'Find the cheapest cloud GPU that fits your model. Pick a model, quantization and context (or enter the VRAM you need) and compare hourly and monthly on-demand prices for RTX 4090, A6000, L40S, A100, H100, H200 and B200 on Runpod, Lambda and AWS.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning', 'Planning'],
    link: '/tools/developer/gpu-rental-cost-calculator',
    icon: '☁️',
    embed: 'card',
    howToSteps: [
      { name: 'Size the job', text: 'Pick a model, quantization and context length, or type in the VRAM your workload needs.' },
      { name: 'Set your hours', text: 'Choose how many hours a day and days a month the GPU will run.' },
      { name: 'Compare providers', text: 'See every GPU configuration that fits, cheapest first, with the hourly and monthly on-demand price on Runpod, Lambda and AWS.' },
    ],
  },
  {
    name: 'LLM GPU Benchmark',
    description: 'Speed test your GPU for AI — measure real memory bandwidth and compute with WebGPU, run an actual LLM in your browser to measure true tokens/sec, and see predicted speeds for every popular model on your hardware.',
    category: 'Developer',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/developer/llm-gpu-benchmark',
    icon: '🏁',
    embed: 'card',
    howToSteps: [
      { name: 'Run the quick test', text: 'A 10-second WebGPU benchmark measures your GPU\'s memory bandwidth and compute throughput — the two numbers that determine LLM speed.' },
      { name: 'Optionally run a real model', text: 'Download a small LLM (300MB-1GB, cached) and generate text on your GPU to measure actual tokens per second.' },
      { name: 'See what it means', text: 'Get predicted native speeds for popular models like Llama, Qwen, and Gemma on your specific hardware.' },
    ],
  },
  // Developer Tools
  {
    name: 'requirements.txt Generator',
    description: 'Generate Python requirements.txt files with popular package presets for Django, FastAPI, Data Science, ML, and more',
    category: 'Developer',
    link: '/tools/developer/requirements-txt-generator',
    icon: '🐍',
  },
  {
    name: 'Dockerfile Generator',
    description: 'Generate optimized Dockerfiles with multi-stage builds, security best practices, and templates for Python, Node.js, Go, and Java',
    category: 'Developer',
    link: '/tools/developer/dockerfile-generator',
    icon: '🐳',
  },
  {
    name: '.env File Generator',
    description: 'Generate .env files with templates for Next.js, Express, Django, Laravel, AWS, Stripe, Firebase, and other platforms',
    category: 'Developer',
    link: '/tools/developer/env-file-generator',
    icon: '⚙️',
  },
  {
    name: 'package.json Generator',
    description: 'Generate package.json files with presets for React, Next.js, Express, testing, Tailwind, and other common configurations',
    category: 'Developer',
    link: '/tools/developer/package-json-generator',
    icon: '📦',
  },
  {
    name: 'Claude Code Config Generator',
    description: 'Generate settings.json, CLAUDE.md, and .mcp.json for Anthropic’s Claude Code CLI — model, permission modes, MCP servers, and a tailored project context file. Optional private in-browser AI.',
    category: 'Developer',
    link: '/tools/developer/claude-code-config-generator',
    embed: 'inline',
    icon: '🤖',
  },
  {
    name: 'Codex CLI Config Generator',
    description: 'Generate config.toml and AGENTS.md for OpenAI’s Codex CLI — model, reasoning effort, approval policy, sandbox mode, and MCP servers. Optional private in-browser AI for the context file.',
    category: 'Developer',
    link: '/tools/developer/codex-cli-config-generator',
    embed: 'inline',
    icon: '🦾',
  },
  {
    name: 'Gemini CLI Config Generator',
    description: 'Generate settings.json and GEMINI.md for Google’s Gemini CLI — model, theme, sandbox, and MCP servers. Optional private in-browser AI for the project context file.',
    category: 'Developer',
    link: '/tools/developer/gemini-cli-config-generator',
    embed: 'inline',
    icon: '✨',
  },
  {
    name: 'Qwen Code Config Generator',
    description: 'Generate ~/.qwen/settings.json and env vars for Qwen Code — Alibaba Model Studio, OpenRouter, or a local Ollama/LM Studio endpoint. OpenAI-compatible setup in seconds.',
    category: 'Developer',
    link: '/tools/developer/qwen-code-config-generator',
    embed: 'inline',
    icon: '🐉',
  },
  {
    name: 'Oh My Pi Config Generator',
    description: 'Generate models.yml and settings.yml for the Oh My Pi (omp) coding agent — custom providers plus role-based model routing (default, smol, plan, commit) with fallback chains.',
    category: 'Developer',
    link: '/tools/developer/oh-my-pi-config-generator',
    embed: 'inline',
    icon: '🥧',
  },
  {
    name: 'Ollama Config Generator',
    description: 'Generate an Ollama Modelfile, server-tuning env vars, and the OpenAI-compatible snippet to point a coding agent (Codex, Qwen Code, Aider) at a local model. Includes coder-model sizing.',
    category: 'Developer',
    link: '/tools/developer/ollama-config-generator',
    embed: 'inline',
    icon: '🦙',
  },
  {
    name: 'MCP Server Config Generator',
    description: 'Generate paste-ready MCP server config for Claude Code, Claude Desktop, VS Code, Cursor, and Codex CLI — stdio (command/args/env) or remote Streamable HTTP — with one-click copy for each client.',
    category: 'Developer',
    link: '/tools/developer/mcp-server-config-generator',
    embed: 'inline',
    icon: '🔌',
  },
  {
    name: 'MCP Server Security Checklist',
    description: 'Assess any MCP server before you connect it — a security scorecard covering source trust, least-privilege tool scope, secrets, OAuth/confused-deputy, version pinning, egress, and monitoring, with a live risk score and copyable report.',
    category: 'Developer',
    link: '/tools/developer/mcp-server-security-checklist',
    embed: 'inline',
    icon: '🛡️',
  },
  {
    name: 'Tool / Function-Schema Builder',
    description: 'Build an LLM tool/function definition visually and export it in OpenAI, Anthropic, and MCP formats — one JSON Schema, three provider envelopes, copy-ready.',
    category: 'Developer',
    link: '/tools/developer/tool-schema-builder',
    embed: 'inline',
    icon: '🧩',
  },
  {
    name: 'Which Agent Protocol Do You Need?',
    description: 'Answer a few questions and get the right agent protocol — MCP, A2A, function calling, or ACP — with what it is for and who built it. MCP and A2A are complementary, and the tool says when to use both.',
    category: 'Developer',
    link: '/tools/developer/agent-protocol-selector',
    embed: 'inline',
    icon: '🧭',
  },
  {
    name: 'A2A Agent Card Generator',
    description: 'Build a Google A2A (Agent2Agent) Agent Card JSON manifest — name, capabilities, input/output modes, and skills — served at /.well-known/agent-card.json. Copy-ready.',
    category: 'Developer',
    link: '/tools/developer/a2a-agent-card-generator',
    embed: 'inline',
    icon: '🪪',
  },
  {
    name: 'MCP Config Validator',
    description: 'Paste your mcpServers JSON and lint it — validates the transport shape, catches common mistakes (url vs httpUrl, args not an array), and flags hard-coded secrets that belong in environment variables.',
    category: 'Developer',
    link: '/tools/developer/mcp-config-validator',
    embed: 'inline',
    icon: '✅',
  },
  {
    name: 'Media Sanitization & Destruction Advisor',
    description: 'NIST SP 800-88 decision-tree wizard that recommends Clear, Purge, or Destroy methods based on media type, data sensitivity, and asset disposition (CISSP Domain 2)',
    category: 'Security',
    link: '/tools/security/media-sanitization-advisor',
    embed: 'card',
    icon: '🧹',
  },
  {
    name: 'Database Inference & Aggregation Simulator',
    description: 'Educational simulator demonstrating how database inference and aggregation attacks work, and how countermeasures protect against them. Run queries against mock databases and see how combining aggregate results can reveal individual records. CISSP Domain 8.',
    category: 'Security',
    link: '/tools/security/database-inference-simulator',
    embed: 'card',
    icon: '🗄️',
  },
  // CISSP Security Tool Suite — New Tools
  {
    name: 'Quantitative Risk Analysis Suite',
    description: 'Calculate Single Loss Expectancy (SLE), Annualized Loss Expectancy (ALE), and Safeguard ROI using quantitative risk formulas. CISSP Domain 1 aligned.',
    category: 'Planning',
    link: '/tools/planning/quantitative-risk-analysis',
    embed: 'card',
    icon: '📊',
  },
  {
    name: 'Penetration Test Scoping Calculator',
    description: 'Scope penetration testing engagements with methodology selection, target inventory, hour estimation, phase breakdown, and Statement of Work generation. CISSP Domain 6.',
    category: 'Planning',
    link: '/tools/planning/pentest-scoping-calculator',
    embed: 'card',
    icon: '🎯',
  },
  {
    name: 'DR Site Cost-Benefit Analyzer',
    description: 'Compare Hot, Warm, Cold, Mobile, and Cloud DR site options with 5-year TCO projections, RTO/RPO alignment, and downtime cost modeling. CISSP Domain 7.',
    category: 'Planning',
    link: '/tools/planning/dr-site-cost-analyzer',
    icon: '🏢',
  },
  {
    name: 'Physical Security Planner (CPTED)',
    description: 'Plan physical security controls across security zones with CPTED principles, barrier calculations, fire suppression selection, lighting plans, and cost estimation. CISSP Domain 3.',
    category: 'Planning',
    link: '/tools/planning/physical-security-planner',
    embed: 'card',
    icon: '🏗️',
  },
  {
    name: 'Microsoft Security Compliance Mapper',
    description: 'Map Microsoft 365 security products to 6 compliance frameworks: NIST CSF 2.0, HIPAA, SOC 2, PCI DSS 4.0, CMMC 2.0, and CIS Controls v8. See compliance gaps, get product recommendations with pricing, and export gap analysis reports.',
    category: 'Planning',
    link: '/tools/planning/microsoft-compliance-mapper',
    embed: 'card',
    icon: '🗺️',
  },
  {
    name: 'Microsoft Security Quote Builder',
    description: 'Build Microsoft 365 security quotes with automatic SKU overlap detection. Calculate pricing for Defender, Entra ID, and Intune products with comparison charts and PDF export.',
    category: 'Planning',
    link: '/tools/planning/microsoft-security-quote-builder',
    icon: '🛡️',
  },
  {
    name: 'Threat Modeling Wizard (STRIDE & DREAD)',
    description: 'Build complete threat models with STRIDE decomposition, DREAD scoring, auto-generated threat libraries, and prioritized mitigations. CISSP Domain 1.',
    category: 'Security',
    link: '/tools/security/threat-modeling-wizard',
    embed: 'card',
    icon: '🛡️',
  },
  {
    name: 'Data Classification Policy Architect',
    description: 'Build data classification policies with Government or Commercial schemas, handling rules per level, compliance overlays (HIPAA, PCI-DSS, GDPR, CMMC), and exportable policy documents. CISSP Domain 2.',
    category: 'Compliance',
    link: '/tools/compliance/data-classification-architect',
    embed: 'card',
    icon: '🏷️',
  },
  {
    name: 'Security Model Decision Matrix',
    description: 'Evaluate which formal security model (Bell-LaPadula, Biba, Clark-Wilson, Brewer-Nash) best fits your requirements with scored comparisons and implementation guidance. CISSP Domain 3.',
    category: 'Security',
    link: '/tools/security/security-model-decision-matrix',
    embed: 'card',
    icon: '🔒',
  },
  {
    name: 'Cryptographic Mode Visualizer',
    description: 'See why ECB encryption mode is insecure through pixel-level visualization. Compare ECB vs CBC with interactive Canvas-based demos and mode comparison tables. CISSP Domain 3.',
    category: 'Security',
    link: '/tools/security/crypto-mode-visualizer',
    icon: '🔐',
  },
  {
    name: 'Federated Identity Architect',
    description: 'Select the right federation protocol (SAML, OIDC, OAuth, Kerberos) with visual auth flow diagrams, protocol comparison, and Kerberos troubleshooter. CISSP Domain 5.',
    category: 'Security',
    link: '/tools/security/federated-identity-architect',
    embed: 'card',
    icon: '🪪',
  },
  {
    name: 'Supply Chain Risk Assessor',
    description: 'Assess supply chain risk across vendors and tiers with NIST SP 800-161 alignment, concentration analysis, portfolio risk scoring, and remediation priorities. CISSP Domain 1.',
    category: 'Compliance',
    link: '/tools/compliance/supply-chain-risk-assessor',
    embed: 'card',
    icon: '🔗',
  },
  // CSV Utility Tools
  {
    name: 'CSV Splitter',
    description: 'Split large CSV files into smaller files by row count — all processing in your browser',
    category: 'Developer',
    link: '/tools/developer/csv-splitter',
    icon: '✂️',
  },
  {
    name: 'CSV Viewer',
    description: 'View, sort, and filter CSV data in an interactive table — upload or paste CSV for instant visualization',
    category: 'Developer',
    link: '/tools/developer/csv-viewer',
    icon: '📋',
  },
  // Legal Document Generators
  {
    name: 'NDA Generator',
    description: 'Generate customizable Non-Disclosure Agreement templates — mutual or one-way with jurisdiction selection',
    category: 'Compliance',
    link: '/tools/compliance/nda-generator',
    icon: '🤝',
  },
  {
    name: 'SOW Generator',
    description: 'Generate Statement of Work templates with deliverables, milestones, and payment terms',
    category: 'Compliance',
    link: '/tools/compliance/sow-generator',
    icon: '📝',
  },
  // PDF Tools
  {
    name: 'PDF Merger',
    description: 'Merge multiple PDF files into a single document — drag and drop, reorder, and combine instantly in your browser',
    category: 'Developer',
    link: '/tools/developer/pdf-merger',
    icon: '📎',
  },
  {
    name: 'PDF Splitter',
    description: 'Split PDF files into individual pages or custom page ranges — all processing in your browser',
    category: 'Developer',
    link: '/tools/developer/pdf-splitter',
    icon: '📄',
  },
  {
    name: 'PDF Page Remover',
    description: 'Remove specific pages from a PDF document — select and delete pages instantly in your browser',
    category: 'Developer',
    link: '/tools/developer/pdf-page-remover',
    icon: '🗑️',
  },
  {
    name: 'PDF Page Rotator',
    description: 'Rotate individual PDF pages by 90, 180, or 270 degrees — fix orientation issues instantly',
    category: 'Developer',
    link: '/tools/developer/pdf-page-rotator',
    icon: '🔄',
  },
  {
    name: 'PDF Compressor',
    description: 'Reduce PDF file size by optimizing structure and removing metadata — client-side compression',
    category: 'Developer',
    link: '/tools/developer/pdf-compressor',
    icon: '📦',
  },
  {
    name: 'PDF to Image Converter',
    description: 'Convert PDF pages to PNG or JPEG images at custom resolutions — all processing in your browser',
    category: 'Developer',
    link: '/tools/developer/pdf-to-image',
    icon: '🖼️',
  },
  {
    name: 'PDF Password Protector',
    description: 'Add password protection to PDF files with customizable permissions — encrypt PDFs in your browser',
    category: 'Developer',
    link: '/tools/developer/pdf-password-protector',
    icon: '🔒',
  },
  {
    name: 'PDF Page Reorderer',
    description: 'Rearrange pages in a PDF document — move pages up, down, or to any position with instant preview',
    category: 'Developer',
    link: '/tools/developer/pdf-page-reorderer',
    icon: '↕️',
  },
  {
    name: 'Sign PDF',
    description: 'Sign a PDF in your browser — draw, type or upload a signature, add the date and initials, and download. Nothing is uploaded',
    category: 'Developer',
    link: '/tools/developer/sign-pdf',
    icon: '✍️',
  },
  {
    name: 'PDF Form Filler',
    description: 'Fill out PDF forms in your browser — text boxes, checkboxes, radio buttons and dropdowns, with optional flattening. Nothing is uploaded',
    category: 'Developer',
    link: '/tools/developer/pdf-form-filler',
    icon: '📝',
  },
  // Image Format Converters
  {
    name: 'SVG to PNG Converter',
    description: 'Convert SVG vector graphics to PNG at any resolution — perfect for icons, logos, and web graphics',
    category: 'Developer',
    link: '/tools/developer/svg-to-png-converter',
    icon: '🎨',
  },
  {
    name: 'PNG to JPG Converter',
    description: 'Convert PNG images to JPG format with adjustable quality — reduce file size while preserving clarity',
    category: 'Developer',
    link: '/tools/developer/png-to-jpg-converter',
    icon: '🖼️',
  },
  {
    name: 'JPG to WebP Converter',
    description: 'Convert JPG images to WebP format for superior web compression — smaller files, same quality',
    category: 'Developer',
    link: '/tools/developer/jpg-to-webp-converter',
    icon: '⚡',
  },
  {
    name: 'WebP to JPG Converter',
    description: 'Convert WebP images to JPG format for universal compatibility — batch processing supported',
    category: 'Developer',
    link: '/tools/developer/webp-to-jpg-converter',
    icon: '🔄',
  },
  {
    name: 'WebP to PNG Converter',
    description: 'Convert WebP images to PNG format preserving transparency — lossless conversion in your browser',
    category: 'Developer',
    link: '/tools/developer/webp-to-png-converter',
    icon: '🔄',
  },

  // Vendor Comparison Tools (datasets registered in lib/data/vendor-datasets/index.ts)
  {
    name: 'Status Page Selector',
    description: 'Find your best status page tool by filtering 8 vendors (Atlassian Statuspage, Better Stack, Instatus, Cachet, and more) by budget, free tier, custom domain, monitoring, and on-call needs.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/status-page-selector',
    icon: '📊',
  },
  {
    name: 'PagerDuty Alternative Selector',
    description: 'Compare PagerDuty alternatives like Opsgenie, incident.io, FireHydrant, Datadog On-Call, Better Stack, Squadcast, and Rootly. Filter by budget, free tier, mobile app, postmortems, and ChatOps.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/pagerduty-alternative-selector',
    icon: '🚨',
  },
  {
    name: 'On-Call Platform Selector',
    description: 'Find your on-call platform from PagerDuty, Opsgenie, incident.io, Splunk On-Call, Grafana OnCall, and others. Filter by budget, integration breadth, AIOps, and escalation depth.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/on-call-platform-selector',
    icon: '📞',
  },
  {
    name: 'Incident Management Tool Selector',
    description: 'Pick an incident management platform — incident.io, FireHydrant, Rootly, Jeli, blameless.io, ServiceNow ITSM, PagerDuty. Filter by postmortem workflow, runbook automation, and integrations.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/incident-management-tool-selector',
    icon: '🔥',
  },
  {
    name: 'ZTNA Provider Selector',
    description: 'Compare Zero Trust Network Access providers including Cloudflare Access, AWS Verified Access, Azure Entra Private Access, Google BeyondCorp, Zscaler, Twingate, and Tailscale.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/ztna-provider-selector',
    icon: '🔐',
  },
  {
    name: 'Cloud Load Balancer Selector',
    description: 'Compare cloud load balancers — Cloudflare LB, AWS ALB/NLB/Global Accelerator, Azure Application Gateway, Azure Front Door, Google Cloud LB. Filter by L4/L7, global vs regional, WAF, and platform.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/cloud-load-balancer-selector',
    icon: '⚖️',
  },
  {
    name: 'Email Service Selector',
    description: 'Pick an email service from Cloudflare Email Routing, AWS SES, Azure Communication Services, Postmark, SendGrid, Mailgun, Resend, Loops, and more. Filter by volume, EU residency, and deliverability tooling.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/email-service-selector',
    icon: '📧',
  },
  {
    name: 'AI Gateway Selector',
    description: 'Find your AI gateway across Cloudflare AI Gateway, Portkey, LiteLLM, Helicone, Langfuse, LangSmith, Bedrock Guardrails, and OpenRouter. Filter by self-hosted, caching, rate limiting, and guardrails.',
    category: 'Vendor Comparison',
    secondary_categories: ['AI & Machine Learning'],
    link: '/tools/comparison/ai-gateway-selector',
    icon: '🤖',
  },
  {
    name: 'MDR Vendor Selector',
    description: 'Compare Managed Detection & Response vendors — CrowdStrike Falcon Complete, Arctic Wolf, Sophos MDR, SentinelOne Vigilance, Red Canary, eSentire, Expel, Huntress, Rapid7, and Mandiant.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/mdr-vendor-selector',
    icon: '🛡️',
  },
  {
    name: 'Cybersecurity Service Selector',
    description: 'Choose between Managed EDR, MDR, XDR, MSSP, co-managed SIEM, in-house SOC, vCISO, and open-source DIY SIEM. Filter by org size, internal team, coverage needs, and budget.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/cybersecurity-service-selector',
    icon: '🏢',
  },
  {
    name: 'SSL Certificate Selector',
    description: 'Compare SSL/TLS certificate providers — Let\'s Encrypt, ZeroSSL, DigiCert, Sectigo, GlobalSign, GeoTrust, Cloudflare SSL, AWS ACM, Entrust. Filter by validation level (DV/OV/EV), wildcard, SAN, and ACME automation.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/ssl-certificate-selector',
    icon: '🔒',
  },
  {
    name: 'Compliance Platform Selector',
    description: 'Find your compliance automation platform across Vanta, Drata, Secureframe, Hyperproof, AuditBoard, Sprinto, OneTrust, Anecdotes, and Strike Graph. Filter by framework support, audit help, and integrations.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/compliance-platform-selector',
    icon: '✅',
  },
  {
    name: 'Python Dependency Manager Selector',
    description: 'Compare Python dependency managers — pip, pip-tools, Poetry, PDM, Hatch, Pipenv, uv (Astral), Conda, Rye, micromamba. Filter by lock files, virtual envs, build/publish, speed, and scientific stack fit.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/python-dependency-manager-selector',
    icon: '🐍',
  },
  {
    name: 'GCP Compute Selector',
    description: 'Pick your GCP compute option across Compute Engine, GKE Standard, GKE Autopilot, Cloud Run, Cloud Functions, and App Engine. Filter by abstraction level, billing model, scaling latency, and workload type.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/gcp-compute-selector',
    icon: '☁️',
  },
  {
    name: 'GCP Container Selector',
    description: 'Find your GCP container platform — GKE Standard, GKE Autopilot, Cloud Run, Cloud Run for Anthos, App Engine Flex, GCE + Docker. Filter by Kubernetes management, app type, and cost model.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/gcp-container-selector',
    icon: '📦',
  },
  {
    name: 'Virtualization Hardware Selector',
    description: 'Pick a virtualization CPU across AMD EPYC 9000/7003, Threadripper Pro, Ryzen 9, Intel Xeon Scalable Gen 4/5, Xeon E, and Core i9. Filter by VM count, max RAM, vendor preference, and ECC.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/virtualization-hardware-selector',
    icon: '💻',
  },
  {
    name: 'Error Tracking Tool Selector',
    description: 'Compare error tracking platforms including GlitchReplay, Sentry, Bugsnag, Rollbar, Honeybadger, Datadog, Raygun, LogRocket, AppSignal. Filter by budget, session replay, source maps, language support, and self-hosted options.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/error-tracking-tool-selector',
    icon: '🐛',
  },
  {
    name: 'Project Management Tool Selector',
    description: 'Pick a PM tool across Planet Roadmap, Linear, Jira, Asana, ClickUp, Monday.com, Notion, Trello, Shortcut, Height, and GitHub Projects. Filter by budget, roadmap views, sprints, OKRs, and integrations.',
    category: 'Vendor Comparison',
    link: '/tools/comparison/project-management-tool-selector',
    icon: '📋',
  },
];

// Helper functions

/**
 * Get all enabled tools (filters out tools with enabled: false)
 */
export function getAllTools(): Tool[] {
  return tools.filter(tool => tool.enabled !== false);
}

export function getToolBySlug(slug: string): Tool | undefined {
  // Match by the final slug portion of the URL (e.g., "jwt-decoder" matches "/tools/security/jwt-decoder")
  return tools.find((tool) => tool.link.endsWith(`/${slug}`));
}

export function getToolsByCategory(category: string): Tool[] {
  return tools.filter((tool) => tool.category === category && tool.enabled !== false);
}

export function getAvailableTools(): Tool[] {
  return tools.filter((tool) => !tool.comingSoon && tool.enabled !== false);
}

export function getToolCategories(): string[] {
  const categories = new Set(tools.map((tool) => tool.category));
  return Array.from(categories).sort();
}

/**
 * Get related tools for a given tool slug
 * @param currentSlug - The slug of the current tool
 * @param limit - Maximum number of related tools to return (default: 3)
 * @returns Array of related tools
 */
export function getRelatedTools(currentSlug: string, limit: number = 3): Tool[] {
  const currentTool = getToolBySlug(currentSlug);

  if (!currentTool) {
    return [];
  }

  // First, get tools from the same category (excluding current, coming soon, and disabled)
  const sameCategory = tools
    .filter(tool =>
      tool.category === currentTool.category &&
      tool.link !== currentTool.link &&
      !tool.comingSoon &&
      tool.enabled !== false
    );

  // If we have enough from same category, return them
  if (sameCategory.length >= limit) {
    return sameCategory.slice(0, limit);
  }

  // Otherwise, fill with popular tools from other categories
  const otherTools = tools
    .filter(tool =>
      tool.link !== currentTool.link &&
      !tool.comingSoon &&
      tool.enabled !== false &&
      !sameCategory.includes(tool)
    )
    .slice(0, limit - sameCategory.length);

  return [...sameCategory, ...otherTools].slice(0, limit);
}

/**
 * Extract slug from tool link
 * @param tool - Tool object
 * @returns Tool slug
 */
export function getToolSlug(tool: Tool): string {
  return tool.link.replace('/tools/', '');
}

// Category helper functions

/**
 * Get all tool categories
 */
export function getAllToolCategories(): ToolCategory[] {
  return toolCategories;
}

/**
 * Get a category by its slug
 */
export function getCategoryBySlug(slug: string): ToolCategory | undefined {
  return toolCategories.find(cat => cat.slug === slug);
}

/**
 * Get tools by category slug (case-insensitive match)
 */
export function getToolsByCategorySlug(slug: string): Tool[] {
  const category = getCategoryBySlug(slug);
  if (!category) return [];
  const categoryNameLower = category.name.toLowerCase();
  return tools.filter(tool => {
    if (tool.enabled === false) return false;
    // Match primary category
    if (tool.category.toLowerCase() === categoryNameLower) return true;
    // Match secondary categories
    if (tool.secondary_categories?.some(cat => cat.toLowerCase() === categoryNameLower)) return true;
    return false;
  });
}

/**
 * Get count of enabled tools in a category
 */
export function getToolCountByCategory(slug: string): number {
  return getToolsByCategorySlug(slug).length;
}

/**
 * Get category slug from category name (case-insensitive)
 * Example: "Security" -> "security", "Developer" -> "developer"
 */
export function getCategorySlugFromName(categoryName: string): string | undefined {
  const category = toolCategories.find(
    cat => cat.name.toLowerCase() === categoryName.toLowerCase()
  );
  return category?.slug;
}

/**
 * Get category by name (case-insensitive)
 * Falls back to slug matching for robustness
 */
export function getCategoryByName(categoryName: string): ToolCategory | undefined {
  if (!categoryName) return undefined;

  const normalizedInput = categoryName.toLowerCase().trim();

  // First try to match by name (case-insensitive)
  const byName = toolCategories.find(
    cat => cat.name.toLowerCase() === normalizedInput
  );
  if (byName) return byName;

  // Fallback: try to match by slug
  return toolCategories.find(
    cat => cat.slug.toLowerCase() === normalizedInput
  );
}
